/**
 * MediaManager — centralized, reliable control for ALL LiveKit media tracks:
 * audio (microphone), video (camera), and screen share.
 *
 * Problem it solves:
 *   - `setMicrophoneEnabled`, `setCameraEnabled`, `setScreenShareEnabled` can fail
 *     silently or throw (permissions, autoplay, device busy, track publish timeout).
 *   - Without retry + verification the UI state and actual track state desync.
 *   - Discord-like UX demands 100% reliability — the user must never need to reload.
 *
 * Design principles:
 *   1. Every operation retries with exponential backoff (max 3 attempts).
 *   2. After each operation we VERIFY ground truth: flag AND track publication.
 *   3. If LiveKit fails we fall back to raw `getUserMedia` + manual track replace
 *      (audio and video only — screen share has no fallback).
 *   4. A periodic health-check reconciles expected vs actual state for all tracks.
 *   5. All failures are logged but NEVER leave the store in a broken state.
 */

import { Room, Track, LocalAudioTrack, type LocalTrackPublication, type LocalVideoTrack } from 'livekit-client'
import type { DenoiserBackendName, DenoiserHandle } from './denoiserBackend'
import { loadDenoiserBackend } from './denoiserBackend'
import { requestSendAgcFade } from './sendAgc'
import { applyAgcOnly } from './agcOnly'
import { getCpuPressure, isCpuBusy, startCpuPressureMonitor } from './cpuPressure'
import { useToastStore } from '../stores/toastStore'
import { detectOsDenoiserFromTrack, type OsDenoiserBrand } from './denoiserRouter'
import { getVideoConfig, readVideoQuality, busyRoomPublishSenderParams, type VideoQuality } from './videoQuality'
import { MediaContext, getLastMicDisable, type MediaContextOptions } from './mediaContext'
import { resumeManagedAudioContexts } from '../components/voice/audioContext'
import { backgroundEngine, setBackgroundEngineSessionOverride } from './backgroundEngineFlag'
import { videoPrivacyEnabled } from './videoPrivacyFlag'
import { enhanceLevels01, resolveEnhance01, anyEnhanceOn, type EnhanceLevels } from './cameraEnhance'

/**
 * Thrown by `setCameraEnabled` / `setScreenShareEnabled` when another
 * MediaContext already owns the camera or screen-share. UI catches this
 * and surfaces a Telegram-style "Camera busy" modal letting the user
 * either cancel or yield the resource to the new caller.
 */
export class CameraBusyError extends Error {
  readonly kind: 'camera' | 'screen'
  readonly ownerLabel: string
  constructor(kind: 'camera' | 'screen', ownerLabel: string) {
    super(`${kind === 'camera' ? 'Camera' : 'Screen share'} is in use by "${ownerLabel}"`)
    this.name = 'CameraBusyError'
    this.kind = kind
    this.ownerLabel = ownerLabel
  }
}

/**
 * OSPROD-271: thrown by `setCameraEnabled(true)` when BOTH bounded enable
 * attempts timed out (camera never initialised — e.g. while a call recording
 * was active the enable pipeline stalled pre-publish and the tile spun
 * forever). The UI catches this and shows a clear "camera didn't start"
 * error instead of an infinite spinner. `stage` names the await that stalled
 * ('private-publish' | 'livekit-enable' | 'fallback-enable') — it is also
 * recorded into voice_diag rawStats {type:'camera-enable-stall'}.
 */
export class CameraEnableTimeoutError extends Error {
  readonly stage: string
  constructor(stage: string, timeoutMs: number) {
    super(`Camera enable timed out after ${timeoutMs}ms (stage: ${stage})`)
    this.name = 'CameraEnableTimeoutError'
    this.stage = stage
  }
}

/**
 * OSPROD-271: per-attempt watchdog for the camera-enable pipeline. Every
 * await on the enable path (gUM, processor init, LiveKit publish) MUST be
 * bounded — a single pending promise (e.g. `video.play()` on a track that
 * never delivers frames) previously left the camera toggle in-flight
 * forever with no error, no retry and no telemetry. The stuck underlying
 * promise is NOT cancelled (impossible for gUM/play) — callers must guard
 * late completion with a generation check (see `_camEnableGeneration`).
 */
const CAMERA_ENABLE_TIMEOUT_MS = 10_000
/** OSPROD-271: above this, a camera enable gets its per-stage breakdown
 *  recorded to voice_diag. Well under the watchdog's 10s so we also capture
 *  the merely-slow enables that never trip it — those are the ones that add
 *  up to the "15-20 seconds and a laggy picture" QA reported. */
const CAMERA_ENABLE_SLOW_MS = 2_000
function withCameraEnableTimeout<T>(stage: string, p: Promise<T>, timeoutMs = CAMERA_ENABLE_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new CameraEnableTimeoutError(stage, timeoutMs)), timeoutMs)
    p.then(
      (v) => { clearTimeout(timer); resolve(v) },
      (e) => { clearTimeout(timer); reject(e) },
    )
  })
}
// voiceStore imports mediaManager, but we only call `.getState()` from
// inside callbacks (no top-level side effects), so the cycle is safe
// for ESM. Used to read participant count for adaptive face-zoom; the
// previous `localStorage`-only pattern doesn't apply because
// participant count isn't persisted.
import { useVoiceStore } from '../stores/voiceStore'
import { computeJoinState } from '../stores/autoFaceCenterCoupling'
import { useAuthStore } from '../stores/authStore'
// Runtime-only read of the busy-room latch (getState() inside methods). The
// import is circular (livekitStore imports mediaManager) but safe: the binding
// is never touched at module-eval time, only inside applyBusyRoomPublishCap.
import { useLivekitStore } from '../stores/livekitStore'
// OSPROD-145: video-only gUM circuit breaker. Wraps the camera-permission
// state so a NotAllowedError ("site permission = Block") trips once and
// stops the retry storm — see cameraPermission.ts for the full rationale.
import {
  isCameraPermissionDenied,
  markCameraPermissionDenied,
  requestVideoStream,
} from './cameraPermission'
// OSPROD-146: keep-publication-alive mute path. Silent dummy track + flag.
// Default OFF behind localStorage 'voiceMicReplaceTrack'. See helper file
// for full rationale + flip-on criteria.
import { getSilentMicTrack, isReplaceTrackMicMuteEnabled } from './silentMicTrack'

interface RetryContext {
  attempt: number
  maxAttempts: number
  baseDelay: number
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function expBackoffDelay(attempt: number, base: number): number {
  return base * 2 ** attempt
}

/**
 * A user dismissing the getUserMedia / getDisplayMedia permission prompt
 * (or hitting Cancel in the OS screen-picker) surfaces as a
 * `NotAllowedError` (Chrome/Edge — also permission-policy denial) or an
 * `AbortError` (Firefox/Safari "Cancel"). These are an explicit "no":
 * retrying just re-opens the picker, which is precisely the OSPROD-97
 * "screen-share needs 3 clicks to cancel" bug. Never retry a cancellation.
 */
function isUserCancellation(err: unknown): boolean {
  const name = (err as { name?: string } | null | undefined)?.name
  return name === 'NotAllowedError' || name === 'AbortError'
}

async function withRetry<T>(
  operation: () => Promise<T>,
  context: RetryContext,
  onError?: (err: unknown, attempt: number) => void,
): Promise<T> {
  let lastError: unknown = null
  for (let i = 0; i < context.maxAttempts; i++) {
    try {
      return await operation()
    } catch (err) {
      lastError = err
      onError?.(err, i)
      // Never retry an explicit user cancellation / permission denial —
      // each retry re-opens the OS prompt or screen-picker (OSPROD-97).
      if (isUserCancellation(err)) throw err
      if (i < context.maxAttempts - 1) {
        await sleep(expBackoffDelay(i, context.baseDelay))
      }
    }
  }
  throw lastError
}

export interface MediaManagerOptions {
  maxRetries?: number
  baseRetryDelayMs?: number
  healthCheckIntervalMs?: number
}

const DEFAULT_OPTIONS: Required<MediaManagerOptions> = {
  maxRetries: 3,
  baseRetryDelayMs: 300,
  // Detection cadence for mic/video/screen desync + denoiser integrity.
  // Was 5000 historically; lowered to 1000 so a dropped denoiser is
  // recovered within ~1 s instead of ~5 s (users could otherwise hear
  // raw, uncompressed mic for up to a tick). Safe to lower because the
  // destructive part — denoiser reapply — is gated by an independent
  // 60-s cooldown (DENOISER_REAPPLY_BACKOFF_MS) + 3-strike budget, so
  // checking 5x more often only speeds up detection, not republish
  // churn. The historic 5 s/SDP-renegotiation stutter (299b956) was
  // fixed by the reapply backoff, not this interval. The checks
  // themselves are cheap (readyState reads + isMicrophoneEnabled).
  healthCheckIntervalMs: 1000,
}

// --- Screen-share lifecycle event bus ---------------------------------------
// Used by VoiceChannelControls to spawn / close the desktop overlay window
// (Electron transparent click-through or web popout) when a screen share
// starts or stops. The bus is intentionally a small set-of-callbacks rather
// than a full EventEmitter — no extra dependency, simple subscribe/unsubscribe.

export interface ScreenShareStartedEvent {
  trackSid: string
  bounds: { x: number; y: number; width: number; height: number; displayId?: string | number }
}

export interface ScreenShareStoppedEvent {
  trackSid: string
}

const screenShareStartedListeners = new Set<(e: ScreenShareStartedEvent) => void>()
const screenShareStoppedListeners = new Set<(e: ScreenShareStoppedEvent) => void>()

export function onScreenShareStarted(fn: (e: ScreenShareStartedEvent) => void): () => void {
  screenShareStartedListeners.add(fn)
  return () => { screenShareStartedListeners.delete(fn) }
}

export function onScreenShareStopped(fn: (e: ScreenShareStoppedEvent) => void): () => void {
  screenShareStoppedListeners.add(fn)
  return () => { screenShareStoppedListeners.delete(fn) }
}

function emitScreenShareStarted(e: ScreenShareStartedEvent): void {
  for (const fn of screenShareStartedListeners) {
    try { fn(e) } catch (err) { console.error('[mediaManager] screen-share-started listener error', err) }
  }
}

function emitScreenShareStopped(e: ScreenShareStoppedEvent): void {
  for (const fn of screenShareStoppedListeners) {
    try { fn(e) } catch (err) { console.error('[mediaManager] screen-share-stopped listener error', err) }
  }
}

// Track the active screen-share sid so we can emit Stopped with the correct id
// even after the publication is gone (LiveKit removes the publication on disable).
let activeScreenShareSid: string = ''
// ---------------------------------------------------------------------------

export type MediaKind = 'audio' | 'video' | 'screen'

/**
 * Observable denoiser state exposed via `mediaManager.getDenoiserState`
 * and `subscribeDenoiser`. The picker badge reads it to show the user
 * which model is actually running (not just which they picked).
 *
 * 'os-krisp' / 'os-nvidia' mean we DETECTED a virtual mic from one of
 * those products and skipped our in-app denoiser entirely — the raw
 * track from that virtual mic is already cleaned. Layering DFN/RNNoise
 * on top creates artefacts. See denoiserRouter.ts.
 */
export type DenoiserStatus = 'rnnoise' | 'agc-only' | 'browser' | 'off' | 'idle' | 'os-krisp' | 'os-nvidia'

export type BackgroundEffect =
  | { kind: 'none' }
  | { kind: 'blur'; strength?: number }
  | { kind: 'image'; imageUrl: string }

class MediaManager {
  private opts: Required<MediaManagerOptions>
  private healthTimer: ReturnType<typeof setInterval> | null = null
  // Denoiser graph handle (RNNoise worklet, future DeepFilterNet Worker,
  // etc.). Held here so toggling noise suppression off or disconnecting
  // can tear it down — leaving it dangling leaks audio nodes across
  // reconnects and eventually crashes the underlying AudioContext.
  // Type is the abstract DenoiserHandle so the same field works for
  // every backend; the concrete implementation lives in
  // services/{rnnoise,deepfilternet}.ts and is loaded via
  // services/denoiserBackend.ts.
  private denoiserHandle: DenoiserHandle | null = null
  // Observable "what noise suppressor is actually running right now"
  // state. Exposed to the UI so the user (or support) can confirm
  // whether the requested backend actually loaded vs silently fell
  // back to a weaker path.
  //
  // Values:
  //   'rnnoise' — RNNoise WASM worklet active.
  //   'dfn'     — DeepFilterNet ONNX session active (when shipped).
  //   'browser' — A denoiser was requested but failed to init; the
  //               track is passed through `noiseSuppression: true`
  //               to getUserMedia and Chromium's native NS handles it.
  //   'off'     — user explicitly toggled noise suppression off.
  //   'idle'    — mic not enabled / not yet enabled this session.
  private _denoiserState: DenoiserStatus = 'idle'
  private _denoiserListeners: Set<(s: DenoiserStatus) => void> = new Set()
  // True when we detected the user's mic is already an OS-level
  // denoiser (Krisp / NVIDIA Broadcast) and chose to publish its
  // output raw. In this state `denoiserHandle` is null on purpose;
  // the health check must skip its integrity probe — there's nothing
  // of ours to verify. Set in `applyDenoiserMic`, cleared whenever
  // any other branch (cascade success, browser-native fallback,
  // explicit off) takes over.
  private _osDenoiserActive = false
  // CPU-driven outgoing-video governor (audio-first). Under sustained heavy
  // CPU we drop the published video to the 'low' preset so the encoder stops
  // starving the CPU that audio needs, and restore the user's preset
  // when the CPU is sustained-nominal. The camera is never turned off — only
  // downscaled. Chrome/Edge only (Compute Pressure API); elsewhere inert.
  private _cpuVideoOverride: VideoQuality | null = null
  private _cpuBusySince = 0 // epoch ms CPU first went serious/critical (0 = not busy)
  // True while a single-layer canvas publish is held at the busy-room 180p cap.
  // Lets RESTORE run even after the background was turned off in-place (which
  // nulls combinedProcessor without republishing) so the sender can't stay low.
  private _busyRoomCapped = false
  private _cpuNominalSince = 0 // epoch ms CPU first went nominal while downscaled (0 = n/a)
  private _lastCpuVideoChangeAt = 0 // epoch ms of the last governor video republish (anti-flap dwell)
  private _lastCpuVideoToastAt = 0
  // Health-check re-apply backoff. Without this the integrity check
  // ran every 5 s and, when its track-identity comparison kept
  // failing (e.g. LiveKit wraps the published track so
  // `pub.track.mediaStreamTrack !== this.denoiserHandle.track` even
  // though both reference the same audio), it would re-apply
  // setMicrophoneEnabled forever. Each re-apply forces a fresh
  // getUserMedia + new DFN context + republish, which triggers an
  // SDP renegotiation that stalls inbound RTP for ~8 s — which then
  // makes remoteAudioWatchdog fire "no bytes for 9s" and
  // re-subscribe every peer, producing audible stutter for the
  // user reported as "ужасные заикания" once DFN activated.
  //
  // Floor of 60 s between re-applies + 3-strikes-and-give-up. After
  // three consecutive failed integrity checks we conclude the loop
  // can't be resolved by re-applying and back off until the next
  // legitimate user-driven change (mute toggle, device switch, NS
  // toggle, manual denoiser-backend change — any of those reset
  // the strike counter via `_resetDenoiserReapplyState`).
  private _lastDenoiserReapplyAt = 0
  private _consecutiveDenoiserReapplyFailures = 0
  private static readonly DENOISER_REAPPLY_BACKOFF_MS = 60_000
  private static readonly DENOISER_REAPPLY_MAX_STRIKES = 3
  // Flap guard for the health-check reconcile loop (#71, 2026-06-22 prod repro).
  // The 60 s / 3-strike storm gate (#37/#38) catches a PERSISTENT desync but NOT a
  // FLAPPING one: when an external path toggles the mic and each flap carries an
  // intervening HEALTHY tick, `_resetReconcile` zeroes lastAt + strikes every cycle,
  // so the gate re-arms and recovers on EVERY ~2 s flap forever (prod: Guest/Safari
  // in "DEV Conference room" — health_recovery_count 0→18 in ~7 min, last_recovery_
  // reason=health-audio-desync = "микрофон сам мьютится"). `recoveryTimes` (below)
  // SURVIVES the healthy-tick reset, so once recoveries cluster we throttle to a
  // bounded spacing instead of fighting every tick — re-asserting the user's
  // intended state at most once per THROTTLE_MS, never stranding the mic muted.
  private static readonly RECONCILE_FLAP_WINDOW_MS = 60_000
  private static readonly RECONCILE_FLAP_MAX = 3
  private static readonly RECONCILE_FLAP_THROTTLE_MS = 20_000
  // Stranded-mic auto re-arm (#72, 2026-06-29 — DEFAULT-ON since 1.2.34A; opt-OUT
  // kill-switch `localStorage 'voiceAutoMicReconnect'='off'`). When audio recovery
  // EXHAUSTS its 3-strike budget the loop gives up until a reconnect / user action —
  // the "#63" stranded state ("stuck 25-45 min until a full page refresh"; the
  // 2026-06-29 standup's Konstantin/Igor "mic didn't turn on → refresh"). After
  // STRANDED_REARM_MS of CONTINUOUS audio desync past the spent budget we grant ONE
  // fresh recovery attempt on a >=STRANDED_REARM_MS cadence, so a stranded mic
  // self-heals in-place. Shipped DORMANT in 1.2.26A, validated live on prod, then
  // flipped DEFAULT-ON in 1.2.34A (ADR 2026-06-29-stranded-mic-auto-rearm-default-on).
  // _audioDesyncSince = first desynced tick (0 = healthy);
  // _lastStrandedRearmAt = last granted re-arm.
  private static readonly STRANDED_REARM_MS = 12_000
  private _audioDesyncSince = 0
  private _lastStrandedRearmAt = 0
  // CPU-driven video governor (audio-first) hysteresis.
  private static readonly CPU_VIDEO_DOWNSCALE_MS = 5_000 // sustained busy → drop video to 'low'
  private static readonly CPU_VIDEO_RESTORE_MS = 90_000 // sustained nominal → restore (raised 30s→90s 2026-05-22: a bursty CPU signal flapped down↔up every ~35s and each republish renegotiated SDP → wrecked AUDIO; #41)
  // Anti-flap: the governor may republish video at most once per this window, in
  // EITHER direction. Sergey QA's Compute-Pressure flapped nominal↔critical every
  // few seconds → downscale→restore→downscale republish storm; each republish is
  // an SDP renegotiation that disrupts AUDIO (the thing the governor exists to
  // protect — it was backfiring). Bounds governor video republishes to ≤1/90s.
  private static readonly CPU_VIDEO_MIN_DWELL_MS = 90_000
  // Per-kind reconcile backoff for the health-check loop. Each kind's desync
  // recovery republishes the track (audio: setMicrophoneEnabled →
  // applyDenoiserMic → getUserMedia; video/screen: setTrackEnabled →
  // getUserMedia for video) — a destructive SDP renegotiation. After the
  // cadence dropped to 1 s (d5e9e53), a *persistent* verifyGroundTruth
  // false-positive fires this EVERY tick: on audio = the mic-jumping /
  // "nobody audible" storm (40/187/139 getUserMedia across 3 dumps, invariant
  // #37); on video = the camera flickering every second (invariant #38).
  // Gate ALL kinds with the shared 60 s backoff + 3-strike budget; reset a
  // kind on its own healthy tick so a genuine transient desync still recovers
  // on the very next tick.
  private _reconcileGate: Record<
    MediaKind,
    { lastAt: number; strikes: number; recoveryTimes?: number[]; lastFlapWarnAt?: number }
  > = {
    audio: { lastAt: 0, strikes: 0, recoveryTimes: [], lastFlapWarnAt: 0 },
    video: { lastAt: 0, strikes: 0, recoveryTimes: [], lastFlapWarnAt: 0 },
    screen: { lastAt: 0, strikes: 0, recoveryTimes: [], lastFlapWarnAt: 0 },
  }
  // TEMP voice-diag (VOICE_DIAG_ENABLED): cumulative getUserMedia + denoiser
  // (re)apply counts for this page. Read-only telemetry surfaced via
  // getDiagnosticCounters() so the diag collector can spot a re-capture
  // storm directly. NEVER gates behavior.
  private _gumCount = 0
  private _denoiserApplyCount = 0
  // Recovery / health-check telemetry — surfaced via getDiagnosticCounters
  // so an incident is readable from ClickHouse, not only from the user's
  // browser console (the Sasha 2026-05-21 "Raw mic track ended" storm was
  // visible in her console but NOT in our metrics). micTrackEnded = real
  // (non-self-inflicted) OS-level mic-track deaths + re-acquire;
  // healthRecovery = health-check reconcile / denoiser re-apply firings;
  // lastRecoveryReason = why the most recent recovery fired.
  private _micTrackEndedCount = 0
  private _healthRecoveryCount = 0
  private _lastRecoveryReason = ''
  // 2026-06-15 DIAGNOSTIC (read-only): the verifyGroundTruth inputs captured at
  // the instant an AUDIO zombie (false desync) fires. Prod showed health-audio-
  // desync churning on every user while mic_track_ended_count=0 (the raw track
  // never died) → the 154A denoiser-handle guard has a gap. This records WHY the
  // guard missed (handle null? processed-track not live? pub muted?) so the next
  // call pinpoints the exact false-positive condition. Does NOT change recovery.
  private _lastAudioGtDiag = ''
  // 2026-06-29 DIAGNOSTIC (read-only): the verifyGroundTruth inputs captured at
  // the instant an AUDIO flag-false desync fires (isMicrophoneEnabled=false while
  // the user INTENDS unmuted, track not ready). This is the OTHER desync branch —
  // distinct from the zombie one above — and was previously UNINSTRUMENTED. The
  // 25-person 2026-06-29 standup ("mic didn't turn on, had to refresh": Konstantin
  // ×6, Igor) couldn't tell whether the universal health-audio-desync churn came
  // from a false 'ended' read or a genuinely un-published mic / a publication that
  // never reached the SFU (ICE never connected). `room=<ConnectionState>` makes
  // that distinction queryable. Does NOT change recovery. Surfaced via
  // getDiagnosticCounters → voice_diag rawStats (type 'audio-desync-diag').
  private _lastAudioDesyncDiag = ''
  // 2026-07-01 OSPROD-248 ("Microphone toggle failed" toast, DEV Conference
  // room): user-facing mic-toggle failures were console-only — the toast fired
  // repeatedly on prod with ZERO telemetry of the underlying rejection (the
  // incident forensics had to reconstruct it from nginx + intent/runtime
  // flips). Every store-level toggle failure now lands here (stage + error)
  // and is surfaced via getDiagnosticCounters → voice_diag rawStats
  // {type:'mic-toggle-fail'}, so the NEXT report comes with the reason.
  private _micToggleFailCount = 0
  private _lastMicToggleFail = ''
  // 2026-07-06 OSPROD-271 ("camera never initialises while Recording is
  // active", infinite tile spinner): a camera-enable attempt that stalls on
  // an unbounded await (video.play() pre-publish, gUM, LiveKit publish) now
  // times out, retries once and — if still stuck — fails loudly. Every
  // timeout lands here (stage + attempt) and is surfaced via
  // getDiagnosticCounters → voice_diag rawStats {type:'camera-enable-stall'}
  // so the NEXT stuck-camera report pinpoints the blocking await.
  private _camEnableStallCount = 0
  private _lastCamEnableStall = ''
  // OSPROD-271 round 2: the stall flag records only THAT an attempt blew the
  // 10s budget, never WHERE. The 2026-07-06 fix bounded the hang without
  // knowing its cause, and the retest showed the cost simply moved into a
  // deterministic 15-20s (10s timeout + a retry that succeeds). Prod still
  // shows stalls on ~8% of sessions with no recording running anywhere, so
  // the recording correlation in the ticket is a red herring. Record a
  // per-stage breakdown of slow enables so the next read points at one await.
  private _camEnableSlowCount = 0
  private _lastCamEnableTiming = ''
  // Generation counter for camera-enable attempts. A timed-out attempt's
  // underlying promise cannot be cancelled; if it completes LATE (after the
  // watchdog fired and a retry already published a fresh track) it must not
  // publish a SECOND camera track. publishCameraWithEffect snapshots the
  // generation at entry and re-checks it before publishing.
  private _camEnableGeneration = 0
  // Session pin: the deviceId we ACTUALLY captured for the mic. Once a mic
  // works, re-acquires (unmute, health-check republish) reuse this exact
  // device instead of the OS default — so enabling a webcam (which on
  // Windows can make its built-in mic the OS "default communications"
  // device) can't hijack the mic mid-call (Sergey QA repro: cam on →
  // StreamCam mic → "очень плохо слышно"). An explicit setAudioDevice()
  // pick overwrites it; if the pinned device vanishes we fall back to the
  // OS default and re-pin. Not persisted — cleared on page reload.
  private _pinnedMicDeviceId: string | null = null
  // The deviceId the USER explicitly chose (saved preference applied on
  // join, or a live setAudioDevice pick) — distinct from _pinnedMicDeviceId
  // (whatever we last physically captured). A transient capture failure on
  // the chosen device (Bluetooth SCO dropout / "communications" profile flip
  // → OverconstrainedError / NotReadableError, classic on AirPods) must NOT
  // permanently demote the user to the OS-default mic. We keep this sticky
  // and keep retrying it on every reconcile, so the moment the device is back
  // we reclaim it. Without this, ONE AirPods hiccup pinned Sergey QA onto the
  // built-in Realtek array for the rest of the session ("очень плохо слышно"
  // until he manually re-picked AirPods every connect — 59× Realtek vs 9×
  // AirPods captures in his webrtc dump). Only an explicit pick changes it.
  private _userMicDeviceId: string | null = null
  // Throttle for the "mic not published, denoiser failed" toast so a
  // retrying health-check doesn't spam it.
  private _lastDenoiserFailToastAt = 0
  // Single-flight chain for setMicrophoneEnabled. webrtc-internals
  // captured ≥7 concurrent getUserMedia calls within a single
  // millisecond during a big call (multiple callers — VoiceManager
  // onConnect, health check, RNNoise integrity, store subscribers —
  // racing). Each parallel gUM created its own MediaStreamTrack;
  // LiveKit only publishes one of them, the rest leak as zombies
  // that the SFU may still be forwarding (peers hearing a "muted"
  // user). Serialising every call through this chain forces the
  // sequence to execute one-at-a-time and lets cleanup of a previous
  // op complete before the next starts.
  //
  // The chain `.catch(() => false)` keeps it alive after a rejected
  // op — without that one failure would poison every subsequent call.
  private micOpChain: Promise<boolean> = Promise.resolve(true)
  // Currently applied background effect — kept here so we can re-apply
  // it after a camera restart (quality switch / device change) without
  // requiring the caller to re-set it.
  private backgroundEffect: BackgroundEffect = { kind: 'none' }
  // Once-per-session guard: seed `backgroundEffect` from the user's persisted
  // setting on the first camera-on so the effect survives a channel join /
  // reload even when BackgroundEffectPicker isn't mounted (the "фон слетает на
  // входе" bug). See _seedBackgroundFromSettings.
  private _bgSeededFromSettings = false
  // Once-per-session guard: seed zoom/pan from the user's persisted profile
  // settings on the first camera-on (OSPROD-232) — mirrors the background
  // seed above so camera framing survives joins/reloads without depending on
  // CameraZoomPicker being mounted.
  private _frameSeededFromSettings = false
  // Camera zoom + pan. Together with backgroundEffect they're applied
  // through a single chained `CombinedProcessor` so peers see zoom AND
  // blur in a single track — earlier the two were mutually exclusive
  // because LiveKit's `setProcessor` only accepts one slot.
  private videoZoom: number = 1
  private videoPanX: number = 0
  private videoPanY: number = 0
  private videoEnhance: EnhanceLevels = { smooth: 0, lowlight: 0, vibrance: 0 } // 0..1 each (OSPROD-109 v2)
  // Active processor instance — kept so a slider can tweak zoom/pan
  // live without a track restart.
  private combinedProcessor: import('./zoomProcessor').CombinedProcessor | null = null
  // The track-processors module is ~few-MB MediaPipe wasm; lazy-load on
  // first use so users who never enable an effect don't pay the cost.
  private processorsModulePromise: Promise<any> | null = null
  // Apple Center Stage-style auto-framing. When true, faceTracker drives
  // zoom + pan from BlazeFace detections on the raw camera feed.
  private autoFaceCenter: boolean = false

  // Per-room facade cache. Stores call `mediaManager.forRoom(room)` to
  // get back a stable MediaContext that owns its own shared-mic handle,
  // per-room mute/speaker state, and lifecycle hooks. WeakMap so a Room
  // that's been disconnected and dereferenced naturally gets GC'd along
  // with its context.
  private contexts = new WeakMap<Room, MediaContext>()

  // Mutex ownership for hardware resources that don't multiplex well.
  // The CTO decision (2026-05-16) is to keep camera and screen as
  // one-room-at-a-time; the second caller gets a CameraBusyError and
  // the UI surfaces a Telegram-style "yield camera?" modal.
  private cameraOwner: MediaContext | null = null
  private screenOwner: MediaContext | null = null

  constructor(opts: MediaManagerOptions = {}) {
    this.opts = { ...DEFAULT_OPTIONS, ...opts }
    // Begin watching CPU pressure (Compute Pressure API) so the DFN
    // degrade/recover logic can avoid promoting back to DFN while the CPU
    // is pinned. Best-effort + idempotent; no-op where the API is absent.
    startCpuPressureMonitor()
  }

  /**
   * Get (or lazily create) the per-room MediaContext. Stores hold this
   * alongside their Room reference and route mic / speaker ops through
   * it instead of calling `mediaManager.setX(room, ...)` directly —
   * the legacy methods stay as a compat shim but mutate
   * process-wide state, which broke when two rooms ran concurrently.
   */
  forRoom(room: Room, opts: MediaContextOptions = {}): MediaContext {
    let ctx = this.contexts.get(room)
    if (!ctx) {
      const newCtx = new MediaContext(room, {
        ...opts,
        onDestroy: () => {
          if (this.cameraOwner === newCtx) this.cameraOwner = null
          if (this.screenOwner === newCtx) this.screenOwner = null
          this.contexts.delete(room)
          try { opts.onDestroy?.() } catch { /* noop */ }
        },
        // Forward per-room denoiser changes into the SINGLETON so legacy UI
        // subscribers (VoiceChannelControls amber-spinner gate via
        // `mediaManager.subscribeDenoiser`, DenoiserBadge) reflect the active
        // backend. Per-room sharedMicCapture path otherwise leaves the
        // singleton at its initial 'idle' for the whole session — that's
        // exactly what produced the perpetual mic "connecting" amber spinner
        // on 1.1.172A while audio actually worked fine through the per-room
        // publish (Dmitriy 2026-05-26).
        onDenoiserChange: (state) => {
          this.setDenoiserState(state)
          try { opts.onDenoiserChange?.(state) } catch { /* noop */ }
        },
      })
      this.contexts.set(room, newCtx)
      ctx = newCtx
    }
    return ctx
  }

  /**
   * Try to acquire the camera for the given context. Throws
   * CameraBusyError if another context already holds it. Stores call
   * this from setCameraEnabled BEFORE doing any actual capture work.
   */
  private claimCamera(ctx: MediaContext): void {
    if (this.cameraOwner && this.cameraOwner !== ctx) {
      throw new CameraBusyError('camera', this.cameraOwner.label)
    }
    this.cameraOwner = ctx
  }

  private releaseCamera(ctx: MediaContext): void {
    if (this.cameraOwner === ctx) this.cameraOwner = null
  }

  private claimScreen(ctx: MediaContext): void {
    if (this.screenOwner && this.screenOwner !== ctx) {
      throw new CameraBusyError('screen', this.screenOwner.label)
    }
    this.screenOwner = ctx
  }

  private releaseScreen(ctx: MediaContext): void {
    if (this.screenOwner === ctx) this.screenOwner = null
  }

  /** True if a context currently owns the camera. UI uses this to disable controls cleanly. */
  hasCameraOwner(): boolean { return this.cameraOwner !== null }
  hasScreenOwner(): boolean { return this.screenOwner !== null }
  getCameraOwnerLabel(): string | null { return this.cameraOwner?.label ?? null }
  getScreenOwnerLabel(): string | null { return this.screenOwner?.label ?? null }

  // Set true at the start of destroyDenoiser, cleared on the next
  // microtask. Suppresses the track-death watcher when the 'ended'
  // event came from our own teardown (rawTrack.stop() inside
  // handle.destroy()) rather than an OS-level revoke.
  private _suppressTrackDeathRecovery = false

  private destroyDenoiser() {
    this._suppressTrackDeathRecovery = true
    if (this.denoiserHandle) {
      try { this.denoiserHandle.destroy() } catch {}
      this.denoiserHandle = null
    }
    // Reset the OS-denoiser flag together with the handle. Callers
    // that want OS-denoiser mode (applyDenoiserMic's skip branch) set
    // the flag AFTER calling destroyDenoiser, so this reset doesn't
    // race with them.
    this._osDenoiserActive = false
    queueMicrotask(() => { this._suppressTrackDeathRecovery = false })
  }

  /**
   * Attach a single-fire 'ended' listener to the live raw mic
   * MediaStreamTrack. When the OS revokes the track (USB unplug,
   * Bluetooth dropout, antivirus block, OS sleep), we re-acquire
   * through the standard mic-op chain. Each subsequent applyDenoiserMic
   * call attaches a fresh listener on the new rawTrack, so this
   * doesn't leak listeners across reconnects.
   *
   * The 30-second back-off protects against pathological loops where
   * getUserMedia keeps handing back a track that immediately ends
   * (broken driver, kiosked mic policy) — three consecutive failures
   * within 30 s and we stop pestering until the next user action.
   */
  private _lastTrackDeathRecoveryAt = 0
  private _consecutiveTrackDeathRecoveries = 0
  private static readonly TRACK_DEATH_BACKOFF_MS = 30_000
  private static readonly TRACK_DEATH_MAX_STRIKES = 3
  private attachMicTrackDeathWatcher(track: MediaStreamTrack, room: Room): void {
    const onEnded = () => {
      track.removeEventListener('ended', onEnded)
      // Suppress if this 'ended' was caused by our own teardown
      // (destroyDenoiser → rawTrack.stop). The flag is set inside
      // destroyDenoiser and cleared on the next microtask, so by the
      // time real OS-level revocations fire (always on a later task),
      // the flag is back to false.
      if (this._suppressTrackDeathRecovery) return
      // Real OS-level track death (not our own teardown). Count it for
      // telemetry so the re-acquire storm is visible in ClickHouse.
      this._micTrackEndedCount++
      this._lastRecoveryReason = 'mic-track-ended'
      const nowMs = Date.now()
      const sinceLast = nowMs - this._lastTrackDeathRecoveryAt
      if (sinceLast > MediaManager.TRACK_DEATH_BACKOFF_MS) {
        // Outside the back-off window — fresh chance to recover.
        this._consecutiveTrackDeathRecoveries = 0
      }
      if (this._consecutiveTrackDeathRecoveries >= MediaManager.TRACK_DEATH_MAX_STRIKES) {
        console.error(
          `[MediaManager] Raw mic track ended ${this._consecutiveTrackDeathRecoveries}× in ${MediaManager.TRACK_DEATH_BACKOFF_MS / 1000}s — giving up auto-recovery until next user action.`,
        )
        return
      }
      this._lastTrackDeathRecoveryAt = nowMs
      this._consecutiveTrackDeathRecoveries += 1
      console.warn(
        `[MediaManager] Raw mic track ended (attempt ${this._consecutiveTrackDeathRecoveries}/${MediaManager.TRACK_DEATH_MAX_STRIKES}). Re-acquiring...`,
      )
      this._reapplyFromHealthCheck(room).catch(() => {})
    }
    track.addEventListener('ended', onEnded)
  }

  // ─────────────────────────────────────────────────────────────
  //  Denoiser status (observable)
  // ─────────────────────────────────────────────────────────────

  /** Current active denoiser. Returns 'idle' before the first mic op. */
  getDenoiserState(): DenoiserStatus {
    return this._denoiserState
  }

  /**
   * TEMP voice-diag counters (page-lifetime, cumulative). gumCount is the
   * re-capture-storm signal — a healthy call sits in the low single digits;
   * the incident dumps showed 40–187. denoiserApplyCount tracks how often
   * the denoiser graph was (re)built.
   */
  getDiagnosticCounters(): {
    gumCount: number
    denoiserApplyCount: number
    micTrackEndedCount: number
    healthRecoveryCount: number
    lastRecoveryReason: string
    denoiserPref: string
    cpuPressure: string
    dfnCooldownMs: number
    cpuVideoOverride: string
    lastMicDisable: string
    lastAudioGtDiag: string
    lastAudioDesyncDiag: string
    micToggleFailCount: number
    lastMicToggleFail: string
    camEnableStallCount: number
    lastCamEnableStall: string
    camEnableSlowCount: number
    lastCamEnableTiming: string
  } {
    return {
      gumCount: this._gumCount,
      denoiserApplyCount: this._denoiserApplyCount,
      micTrackEndedCount: this._micTrackEndedCount,
      healthRecoveryCount: this._healthRecoveryCount,
      lastRecoveryReason: this._lastRecoveryReason,
      lastAudioGtDiag: this._lastAudioGtDiag,
      lastAudioDesyncDiag: this._lastAudioDesyncDiag,
      micToggleFailCount: this._micToggleFailCount,
      lastMicToggleFail: this._lastMicToggleFail,
      camEnableStallCount: this._camEnableStallCount,
      lastCamEnableStall: this._lastCamEnableStall,
      camEnableSlowCount: this._camEnableSlowCount,
      lastCamEnableTiming: this._lastCamEnableTiming,
      // Routing-decision visibility for voice_diag. `denoiserPref` is now
      // pinned to 'rnnoise' since DFN was removed in 1.1.177A — kept as a
      // schema-stable field so existing ClickHouse queries / dashboards
      // don't break. `dfnCooldownMs` always 0 for the same reason.
      denoiserPref: 'rnnoise',
      cpuPressure: getCpuPressure(),
      dfnCooldownMs: 0,
      cpuVideoOverride: this._cpuVideoOverride ?? '',
      lastMicDisable: getLastMicDisable(),
    }
  }

  /**
   * OSPROD-248: record a user-facing mic-toggle failure (any stage — the
   * LiveKit runtime op, its timeout, or the settings persistence) so the
   * "Microphone toggle failed" toast is never a telemetry dead end again.
   * Read by voiceTelemetry.collectSample → voice_diag rawStats
   * {type:'mic-toggle-fail'}. Best-effort; never throws into the toggle path.
   */
  noteMicToggleFailure(stage: string, err: unknown): void {
    try {
      this._micToggleFailCount += 1
      const e = err as { name?: string; message?: string; code?: string } | null
      const detail = e
        ? `${e.name ?? 'Error'}${e.code ? `(${e.code})` : ''}: ${String(e.message ?? err).slice(0, 200)}`
        : String(err).slice(0, 200)
      this._lastMicToggleFail =
        `${new Date().toISOString().slice(11, 23)}|${stage}|${detail}|n=${this._micToggleFailCount}`
      console.warn(`[MediaManager] mic toggle failure (${stage}):`, detail)
    } catch { /* telemetry must never affect the toggle */ }
  }

  /**
   * OSPROD-271: record a camera-enable stall (an await on the enable path
   * exceeded the watchdog). Read by voiceTelemetry.collectSample →
   * voice_diag rawStats {type:'camera-enable-stall'}. Best-effort; never
   * throws into the enable path.
   */
  noteCameraEnableStall(stage: string, detail: string): void {
    try {
      this._camEnableStallCount += 1
      this._lastCamEnableStall =
        `${new Date().toISOString().slice(11, 23)}|${stage}|${String(detail).slice(0, 200)}|n=${this._camEnableStallCount}`
      console.warn(`[MediaManager] camera enable stalled (${stage}):`, detail)
    } catch { /* telemetry must never affect the enable */ }
  }

  /**
   * OSPROD-271: record a per-stage breakdown of a SLOW camera enable
   * (`stage=ms|stage=ms|…`). Complements noteCameraEnableStall, which only
   * says an attempt blew the budget — this says which await ate the time.
   * Written for enables over CAMERA_ENABLE_SLOW_MS so the fast path stays
   * silent. Read via voice_diag rawStats {type:'camera-enable-timing'}.
   * Best-effort; never throws into the enable path.
   */
  noteCameraEnableTiming(breakdown: string, totalMs: number): void {
    try {
      this._camEnableSlowCount += 1
      this._lastCamEnableTiming =
        `${new Date().toISOString().slice(11, 23)}|total=${Math.round(totalMs)}|${breakdown}|n=${this._camEnableSlowCount}`
      console.warn(`[MediaManager] slow camera enable (${Math.round(totalMs)}ms):`, breakdown)
    } catch { /* telemetry must never affect the enable */ }
  }

  /**
   * Subscribe to denoiser state changes. Listener fires immediately
   * with the current state so callers don't have to seed their own
   * initial read. Returns an unsubscribe fn.
   */
  subscribeDenoiser(fn: (s: DenoiserStatus) => void): () => void {
    this._denoiserListeners.add(fn)
    fn(this._denoiserState)
    return () => { this._denoiserListeners.delete(fn) }
  }

  private setDenoiserState(next: DenoiserStatus) {
    if (this._denoiserState === next) return
    this._denoiserState = next
    // info-level so it surfaces in DevTools without being warning-styled,
    // and ALWAYS logs (warns are sometimes filtered by users). The string
    // is intentionally greppable: support can ask "open DevTools → search
    // 'Denoiser:' " on a misbehaving client.
    console.info('[MediaManager] Denoiser:', next)
    for (const fn of this._denoiserListeners) {
      try { fn(next) } catch { /* never let one buggy subscriber kill notify */ }
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  Public API — Audio
  // ─────────────────────────────────────────────────────────────

  async setMicrophoneEnabled(
    room: Room,
    enabled: boolean,
    options?: { deviceId?: string | null; noiseSuppression?: boolean },
  ): Promise<boolean> {
    // Phase 2D — route through per-room MediaContext so two
    // concurrent calls (voice channel + DM) each get their own mic
    // single-flight chain + shared-capture handle. The legacy
    // singleton path below (_setMicrophoneEnabledInner) is no longer
    // reached for the public mic API; we keep the method defined for
    // the health-check internal re-apply path (_reapplyFromHealthCheck)
    // which deliberately uses the global single-flight chain to
    // serialise its own retries.
    return this.forRoom(room).setMicrophoneEnabled(enabled, options)
  }

  /**
   * @deprecated Internal. Kept only for the health-check re-apply
   * path. Migrated public callers go through `forRoom(room).setMicrophoneEnabled`.
   */
  async _legacySetMicrophoneEnabled(
    room: Room,
    enabled: boolean,
    options?: { deviceId?: string | null; noiseSuppression?: boolean },
  ): Promise<boolean> {
    this._resetDenoiserReapplyState()
    const next = this.micOpChain.then(() =>
      this._setMicrophoneEnabledInner(room, enabled, options),
    )
    this.micOpChain = next.catch(() => false)
    return next
  }

  private _resetDenoiserReapplyState(): void {
    this._lastDenoiserReapplyAt = 0
    this._consecutiveDenoiserReapplyFailures = 0
    this._resetReconcile('audio')
    this._resetReconcile('video')
    this._resetReconcile('screen')
    // Discrete legitimate event (LiveKit reconnect via resetRecoveryBudget, or a
    // user mic-toggle via the legacy path): clear the flap window too so the next
    // recovery isn't throttled by stale flap history (#71).
    for (const k of ['audio', 'video', 'screen'] as const) {
      this._reconcileGate[k].recoveryTimes = []
      this._reconcileGate[k].lastFlapWarnAt = 0
    }
  }

  /**
   * Called on a LiveKit reconnect (`RoomEvent.Reconnected`). A reconnect is a
   * discrete, legitimate reason to grant the health-check audio/video/screen
   * recovery a FRESH strike budget. Without it, a desync that already spent the
   * 3-strike budget BEFORE the reconnect stays given-up forever — the health
   * check (which drives off the persisted mute INTENT, not the post-reconnect
   * runtime flag) never retries, so the mic is stranded in 'idle' until a full
   * page refresh. 2026-06-05 big-call incident: a reconnect storm left ~5 web
   * users unable to unmute for 25-45 min, all with `last_recovery_reason =
   * health-audio-desync` + 0 packets out. Resetting the per-kind reconcile gate
   * + denoiser-reapply counters re-arms the 1 s health-check loop; the 60 s /
   * 3-strike storm guard (#37/#38) re-applies immediately, so this grants at
   * most ONE fresh recovery burst per reconnect — never a per-tick storm.
   * See VOICE_CRITICAL_INVARIANTS.md #63.
   */
  resetRecoveryBudget(): void {
    this._resetDenoiserReapplyState()
  }

  // Health-check reconcile gate (per kind). Returns true if a recovery for this
  // kind is allowed NOW (and records the attempt); false within the 60 s window
  // or after the 3-strike budget is spent. See `_reconcileGate` + invariants
  // #37 (audio storm) / #38 (video flicker).
  private _allowReconcile(kind: MediaKind): boolean {
    const g = this._reconcileGate[kind]
    const nowMs = Date.now()
    // Flap window (#71): age out recoveries older than the window. This array is
    // deliberately NOT cleared by `_resetReconcile` (the healthy-tick reset), so a
    // mute/unmute FLAP — which the strike budget can't catch because each flap has
    // an intervening healthy tick that re-arms the budget — is still bounded.
    const recent = (g.recoveryTimes ??= [])
    while (recent.length > 0 && nowMs - recent[0] > MediaManager.RECONCILE_FLAP_WINDOW_MS) recent.shift()

    if (g.strikes >= MediaManager.DENOISER_REAPPLY_MAX_STRIKES) return false
    if (nowMs - g.lastAt < MediaManager.DENOISER_REAPPLY_BACKOFF_MS) return false
    // Once recoveries cluster (FLAP), throttle to >= RECONCILE_FLAP_THROTTLE_MS
    // spacing instead of recovering on every flap tick. Tagged into
    // last_recovery_reason so the underlying disabler stays chaseable in ClickHouse
    // (zero schema change — a new LowCardinality value). See invariant #71.
    if (
      recent.length >= MediaManager.RECONCILE_FLAP_MAX &&
      nowMs - recent[recent.length - 1] < MediaManager.RECONCILE_FLAP_THROTTLE_MS
    ) {
      this._lastRecoveryReason = `health-${kind}-flap-throttle`
      if (nowMs - (g.lastFlapWarnAt ?? 0) >= MediaManager.RECONCILE_FLAP_THROTTLE_MS) {
        g.lastFlapWarnAt = nowMs
        console.warn(
          `[MediaManager] ${kind} recovery FLAP — ${recent.length} reconciles in ` +
            `${Math.round(MediaManager.RECONCILE_FLAP_WINDOW_MS / 1000)}s; throttling to ` +
            `>=${Math.round(MediaManager.RECONCILE_FLAP_THROTTLE_MS / 1000)}s spacing. An external ` +
            `path is toggling ${kind} against intent — see VOICE_CRITICAL_INVARIANTS.md #71.`,
        )
      }
      return false
    }
    g.lastAt = nowMs
    g.strikes += 1
    recent.push(nowMs)
    return true
  }

  private _resetReconcile(kind: MediaKind): void {
    // NOTE (#71): do NOT clear `recoveryTimes`/`lastFlapWarnAt` here. This runs on
    // every HEALTHY tick; clearing the flap window would re-arm immediate recovery
    // each flap cycle and defeat the throttle. The window ages out by time, or is
    // cleared on a discrete reconnect/user-toggle via `_resetDenoiserReapplyState`.
    this._reconcileGate[kind].lastAt = 0
    this._reconcileGate[kind].strikes = 0
  }

  /**
   * Stranded-mic auto re-arm (#72) — DEFAULT-ON since 1.2.34A; opt-OUT via
   * `localStorage 'voiceAutoMicReconnect'='off'`. Called from the health loop on a
   * desynced AUDIO tick, BEFORE `_allowReconcile`. When the audio recovery budget
   * is spent (strikes >= MAX, i.e. the loop has GIVEN UP) and the desync has
   * persisted >= STRANDED_REARM_MS, grant ONE fresh attempt (clear the audio gate +
   * flap window) so the immediately-following `_allowReconcile('audio')` returns
   * true and re-publishes — instead of leaving the mic stranded until a reconnect /
   * user action / page refresh. Rate-limited to one re-arm per STRANDED_REARM_MS.
   * Audio-only and binary (verifyGroundTruth already returned != expected this
   * tick, so there's no DTX/quiet-speaker ambiguity). Validated live on prod
   * 1.2.26A (2026-06-29) before the default flipped on. See VOICE_CRITICAL_INVARIANTS #72.
   */
  private _maybeRearmStrandedAudio(): void {
    // DEFAULT-ON since 1.2.34A (validated live on prod 1.2.26A, 2026-06-29: recovered
    // stranded mics without storming). Now opt-OUT: kill-switch
    // `localStorage 'voiceAutoMicReconnect'='off'` disables it.
    if (typeof localStorage !== 'undefined' && localStorage.getItem('voiceAutoMicReconnect') === 'off') return
    const since = this._audioDesyncSince
    if (!since) return
    const nowMs = Date.now()
    if (nowMs - since < MediaManager.STRANDED_REARM_MS) return
    const g = this._reconcileGate.audio
    // Only act once the NORMAL path has actually given up — otherwise it's already
    // recovering and we'd just double up.
    if (g.strikes < MediaManager.DENOISER_REAPPLY_MAX_STRIKES) return
    if (nowMs - this._lastStrandedRearmAt < MediaManager.STRANDED_REARM_MS) return
    this._lastStrandedRearmAt = nowMs
    // Grant exactly one fresh attempt: clear the strike budget, backoff AND the flap
    // window so the following `_allowReconcile('audio')` this same tick returns true.
    g.strikes = 0
    g.lastAt = 0
    g.recoveryTimes = []
    g.lastFlapWarnAt = 0
    this._lastRecoveryReason = 'health-audio-stall-rearm'
    console.warn(
      `[MediaManager] Audio stranded ${Math.round((nowMs - since) / 1000)}s after recovery budget ` +
        `exhausted — granting one fresh re-publish attempt (voiceAutoMicReconnect). See invariant #72.`,
    )
  }

  /**
   * Internal re-apply path used by the health-check loop. Same
   * single-flight chain as the public `setMicrophoneEnabled` so we
   * don't race a user-driven mute toggle, but DOES NOT reset the
   * denoiser-reapply backoff counter — that's the whole point of
   * tracking strikes.
   */
  private _reapplyFromHealthCheck(room: Room): Promise<boolean> {
    const next = this.micOpChain.then(() =>
      // forceCascade: a re-apply is never a cold "first" apply — go straight to
      // the DFN-first cascade, never re-trigger the RNNoise startup stopgap.
      this._setMicrophoneEnabledInner(room, true, { forceCascade: true }),
    )
    this.micOpChain = next.catch(() => false)
    return next
  }

  private async _setMicrophoneEnabledInner(
    room: Room,
    enabled: boolean,
    options?: { deviceId?: string | null; noiseSuppression?: boolean; forceCascade?: boolean },
  ): Promise<boolean> {
    // Disabling the mic must FULLY release the OS-level capture, not
    // just flip an enabled flag. Otherwise Windows' communications
    // policy keeps ducking every other audio stream (Discord, browser
    // movies, system sounds) by ~30% the whole time we're "muted",
    // and the noise-suppression DSP keeps running. We:
    //   1. Tear down the RNNoise graph (which now also stops the raw
    //      mic track that fed it — see services/rnnoise.ts).
    //   2. Stop EVERY published mic track + unpublish EVERY mic
    //      publication so a previously-leaked publication can't keep
    //      forwarding to peers ("I muted but they still hear me"
    //      big-call report). LiveKit normally maintains one
    //      publication per source, but the race above could leave
    //      orphaned ones.
    //   3. Sync LiveKit's flag so `isMicrophoneEnabled` returns false.
    // On unmute, setMicrophoneEnabled(true) takes the create-and-
    // publish path — there's a 200-500 ms re-acquisition delay, which
    // the user explicitly accepted as the price for not blocking
    // their other audio while muted.
    if (!enabled) {
      // OSPROD-146: kill-switch — when on, keep the mic publication alive
      // and replaceTrack(silent) instead of unpublishing. Avoids the SDP
      // renegotiation that flickers the user's outgoing camera on every
      // unmute (the user-visible symptom on multi-participant calls).
      // The OS mic capture is STILL released — the silent track keeps
      // the RTCRtpSender warm with zero-amplitude audio while the real
      // device is stop()'d, so Windows comms doesn't keep ducking other
      // streams. Default OFF behind localStorage 'voiceMicReplaceTrack'.
      if (isReplaceTrackMicMuteEnabled()) {
        return this._muteMicByReplaceTrack(room)
      }
      this.destroyDenoiser()
      const participant = room.localParticipant
      // Defensive: iterate ALL publications and tear down every one
      // whose source is Microphone. Single-flight above should keep
      // this to one publication in practice, but we also want this
      // path to self-heal after older buggy versions or a future race
      // re-introduces leaks. `getLocalPublication` only returns the
      // first match, which is what let zombies survive a mute.
      const micPubs: LocalTrackPublication[] = Array.from(
        participant.trackPublications.values(),
      ).filter(
        (p): p is LocalTrackPublication =>
          p.kind === Track.Kind.Audio && p.source === Track.Source.Microphone,
      )
      for (const pub of micPubs) {
        const track = pub.track
        if (!track) continue
        try { track.stop() } catch { /* noop */ }
        try {
          await participant.unpublishTrack(track, true)
        } catch (err) {
          console.warn('[MediaManager] mic unpublish on mute failed:', (err as Error)?.message ?? err)
        }
      }
      try { await participant.setMicrophoneEnabled(false) } catch {
        // setMicrophoneEnabled(false) AFTER unpublish is sometimes a
        // no-op; that's fine — the track is gone, the flag will sync
        // on the next observation.
      }
      this.setDenoiserState('idle')
      return false
    }

    // RNNoise activation policy: explicit option wins; when the caller
    // omitted noiseSuppression we fall back to the user's persistent
    // preference (localStorage 'noiseSuppression', mirrored by voiceStore
    // at init). Reading directly from localStorage avoids a circular
    // import (voiceStore → mediaManager → voiceStore) and matches
    // voiceStore's own init read so the two never disagree.
    //
    // Why this matters: every authStore/livekitStore/dmCallStore mute
    // toggle calls setMicrophoneEnabled(room, true) without options.
    // Treating undefined as "off" used to silently strip RNNoise on the
    // first unmute after rejoin/reconnect/PTT release, leaving the user
    // on a bare browser mic without ever telling them.
    const explicitNS: boolean | undefined = options?.noiseSuppression
    const useDenoiser = typeof explicitNS === 'boolean'
      ? explicitNS
      : (typeof window !== 'undefined' && localStorage.getItem('noiseSuppression') !== 'false')
    if (useDenoiser) {
      const used = await this.applyDenoiserMic(room, options?.deviceId || undefined, options?.forceCascade ?? false)
      if (used) {
        this.setDenoiserState(used)
        // OSPROD-146: when the publication was kept alive on the previous
        // mute (silent-track path), applyDenoiserMic took the replaceTrack
        // branch and the underlying MediaStreamTrack is now our denoiser
        // handle — but the publication's isMuted flag is still TRUE from
        // setMicrophoneEnabled(false) at mute time. replaceTrack does NOT
        // clear isMuted. Without an explicit unmute the user is still
        // silent on the wire and isMicrophoneEnabled() reports false.
        // No-op in the legacy path (publishTrack creates the pub fresh
        // with isMuted=false).
        await this._ensureMicUnmuted(room)
        return this.verifyGroundTruth(room, 'audio', true)
      }
      // Denoiser-required policy (big-call decision): every backend in the
      // cascade failed to init (slow net, blocked WASM, model fetch,
      // AudioContext refused). Do NOT fall back to publishing a raw /
      // browser-NS-only mic — on a 20-person call one un-denoised mic
      // blasts that person's background noise to everyone ("треш у всех").
      // Keep the mic UN-published; the gated health-check reconcile retries
      // applyDenoiserMic, so a transient WASM/model load recovers on a
      // later tick (no storm — it's strike+backoff gated). If it never
      // loads the user stays silent on purpose (silence > raw noise for
      // the call) and is told to refresh.
      console.warn('[MediaManager] denoiser cascade failed — NOT publishing mic (denoiser-required policy). Health-check will retry.')
      this.destroyDenoiser()
      this.setDenoiserState('idle')
      const nowMs = Date.now()
      if (nowMs - this._lastDenoiserFailToastAt > 20_000) {
        this._lastDenoiserFailToastAt = nowMs
        try {
          useToastStore.getState().showToast({
            id: 'denoiser-required-mic-off',
            type: 'error',
            title: 'Микрофон не включён',
            body: 'Шумоподавление не загрузилось — звук не отправляется, чтобы не шуметь в звонок. Обнови страницу (Ctrl+Shift+R).',
          })
        } catch { /* toast is best-effort */ }
      }
      return false
    }

    // Native path (browser noise suppression OR explicitly off). Always
    // destroy any lingering RNNoise graph from a previous toggle so the
    // mic source the browser publishes is the bare device, not the
    // worklet-wrapped one.
    this.destroyDenoiser()
    const micOptions: {
      deviceId?: string
      noiseSuppression?: boolean
      echoCancellation?: boolean
      autoGainControl?: boolean
    } = {
      echoCancellation: true,
      autoGainControl: true,
    }
    if (options?.deviceId) micOptions.deviceId = options.deviceId
    // When the user has the toggle "off" we honour it literally — pass
    // noiseSuppression: false. When they have it "on" but RNNoise
    // failed to init, we set it true so the browser does its best.
    micOptions.noiseSuppression = useDenoiser ? true : false

    // Reflect which path actually ran. useDenoiser=true here means the
    // user wanted some denoiser but every backend in the cascade
    // failed → we're on browser-native NS. useDenoiser=false means
    // they explicitly turned NS off → no suppression at all (browser
    // NS also disabled above).
    this.setDenoiserState(useDenoiser ? 'browser' : 'off')

    return this.setTrackEnabled(room, 'audio', true, micOptions)
  }

  /**
   * OSPROD-146: mute the mic by swapping the underlying MediaStreamTrack
   * on the existing publication to a silent dummy, then stop the real OS
   * capture. The publication / RTCRtpSender stays alive → no SDP
   * renegotiation → no video flicker on receivers' side when the user
   * unmutes later.
   *
   * Acts only when an existing mic publication is found. If there isn't
   * one (first-mute pathology, race during reconnect), falls back to the
   * legacy unpublish path so we still mute correctly somehow — the silent
   * track is meaningless without a publication to attach it to.
   *
   * Gated by the `voiceMicReplaceTrack` localStorage flag (default OFF);
   * the caller in _setMicrophoneEnabledInner only takes this branch when
   * `isReplaceTrackMicMuteEnabled()` returns true.
   */
  private async _muteMicByReplaceTrack(room: Room): Promise<boolean> {
    this.destroyDenoiser()
    const participant = room.localParticipant
    const micPubs: LocalTrackPublication[] = Array.from(
      participant.trackPublications.values(),
    ).filter(
      (p): p is LocalTrackPublication =>
        p.kind === Track.Kind.Audio && p.source === Track.Source.Microphone,
    )

    // No existing publication: nothing to replaceTrack onto. Take the
    // simplest mute path that just clears the LiveKit flag, so callers
    // see isMicrophoneEnabled=false. A subsequent unmute will publish
    // fresh via applyDenoiserMic the normal way.
    if (micPubs.length === 0) {
      try { await participant.setMicrophoneEnabled(false) } catch { /* noop */ }
      this.setDenoiserState('idle')
      return false
    }

    const silent = getSilentMicTrack()
    let swapFailed = false
    for (const pub of micPubs) {
      if (!(pub.track instanceof LocalAudioTrack)) continue
      const oldStreamTrack = pub.track.mediaStreamTrack
      try {
        // No SDP renegotiation — same sender, different MediaStreamTrack.
        // The track.replaceTrack signature takes the underlying
        // MediaStreamTrack (NOT a LocalTrack) for raw swaps.
        await pub.track.replaceTrack(silent)
      } catch (err) {
        console.warn('[MediaManager] mute via replaceTrack failed; falling back to unpublish:', err)
        swapFailed = true
        try { pub.track.stop() } catch { /* noop */ }
        try { await participant.unpublishTrack(pub.track, true) } catch { /* noop */ }
        continue
      }
      // Release the OS capture — the silent track now drives the sender,
      // so we don't need the real mic anymore. This is the bit that
      // matters for Windows comms-ducking (#677-694 in legacy path); the
      // historical reason mute was chosen as unpublish in the first place.
      // We get the same OS-level release here, just without the renegotiation.
      if (oldStreamTrack && oldStreamTrack !== silent) {
        try { oldStreamTrack.stop() } catch { /* noop */ }
      }
    }
    // setMicrophoneEnabled(false) flips publication.isMuted=true, which
    // signals to the SFU that the track is muted. Peers receive RTCP-only
    // "muted" markers; Opus DTX downshifts the silent stream to header
    // bytes only. UI's isMicrophoneEnabled correctly reads false.
    try { await participant.setMicrophoneEnabled(false) } catch { /* noop */ }
    this.setDenoiserState('idle')
    void swapFailed // explicit ack — we already logged + recovered above
    return false
  }

  /**
   * OSPROD-146: idempotent "make sure the mic publication is unmuted".
   * Called after applyDenoiserMic on the unmute path so the keep-pub-alive
   * mute path (silent-track replaceTrack + setMicrophoneEnabled(false))
   * doesn't leave the publication stuck in isMuted=true. No-op when the
   * publication doesn't exist or is already unmuted.
   */
  private async _ensureMicUnmuted(room: Room): Promise<void> {
    const pub = this.getLocalPublication(room.localParticipant, 'audio')
    if (!pub?.track || !pub.track.isMuted) return
    try {
      await pub.track.unmute()
    } catch (err) {
      console.warn('[MediaManager] post-applyDenoiserMic track.unmute failed:', err)
    }
  }

  /**
   * Build a denoiser-cleaned audio track from the chosen mic and
   * either replace the existing publication's underlying
   * MediaStreamTrack or publish a fresh one.
   *
   * The backend cascade: try the user's preferred backend first; if
   * its `apply` returns null (init failure — worklet wouldn't load,
   * model wouldn't fetch, etc.) and the preferred backend was the
   * heavier `'dfn'`, fall through to `'rnnoise'` before giving up.
   * Returns the name of the backend that actually succeeded, or
   * null if every backend failed so the caller can drop to browser
   * native NS.
   *
   * Preferred backend is read directly from localStorage (key
   * `denoiserBackend`) to avoid a circular voiceStore→mediaManager
   * import. Defaults to 'rnnoise' so a missing key keeps the
   * historical behaviour.
   */
  private async applyDenoiserMic(
    room: Room,
    deviceId?: string,
    forceCascade = false,
  ): Promise<DenoiserBackendName | 'os-krisp' | 'os-nvidia' | null> {
    let rawTrack: MediaStreamTrack | null = null
    try {
      // Startup fade-in: on a FRESH user enable (unmute / first join) ask the
      // send-AGC graph we're about to build to publish a brief silent→ramp
      // window, so the capture/denoiser/AGC startup transient ("first 1-2 s
      // crackle on mic enable") never reaches peers. NOT on a health-check
      // re-apply (forceCascade) — that fires mid-call and a fade would clip
      // live speech. Tunable: localStorage 'voiceMicFadeInMs' (default 1000 ms,
      // 0 disables).
      requestSendAgcFade(
        forceCascade
          ? 0
          : (() => {
              if (typeof localStorage === 'undefined') return 1000
              const v = parseInt(localStorage.getItem('voiceMicFadeInMs') || '', 10)
              return Number.isFinite(v) ? v : 1000
            })(),
      )
      // Effective device: an explicit pick wins; otherwise reuse the
      // session-pinned device (the one we actually captured last time) so an
      // OS-default flip (e.g. enabling a webcam makes its built-in mic the
      // Windows default) can't hijack the mic on re-acquire. Only when
      // neither is set do we fall to the OS default (first capture).
      // An explicit deviceId (saved preference applied on join, or a live
      // setAudioDevice pick) is the user's sticky intent — remember it so a
      // transient fallback can't erase the choice (see `_userMicDeviceId`).
      if (deviceId) this._userMicDeviceId = deviceId
      // Effective device priority: explicit pick (this op) → the user's
      // sticky choice → the last device we actually captured → OS default
      // (first capture only).
      const effectiveDeviceId =
        deviceId ?? this._userMicDeviceId ?? this._pinnedMicDeviceId ?? undefined
      const buildAudioConstraints = (dev?: string): MediaStreamConstraints => ({
        audio: {
          // Explicitly disable browser-side suppression — every backend
          // does its own NS and stacking the two costs CPU and dulls
          // consonants. Echo cancellation + AGC are still browser
          // territory, those stay on.
          noiseSuppression: false,
          echoCancellation: true,
          autoGainControl: true,
          ...(dev ? { deviceId: { exact: dev } } : {}),
        },
      })
      this._gumCount++
      this._denoiserApplyCount++
      let rawStream: MediaStream
      // True when the requested device threw and we captured the OS default
      // instead — that capture must NOT overwrite the user's pinned choice.
      let fellBackToDefault = false
      try {
        rawStream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(effectiveDeviceId))
      } catch (err) {
        // Pinned/explicit device vanished or is held by another app
        // (unplugged, BT SCO / "communications" profile flip, another app
        // grabbed it) → OverconstrainedError / NotFoundError /
        // NotReadableError. Don't leave the user mic-less: capture the OS
        // default for THIS op so they're audible, but keep the pin on their
        // chosen device so the next reconcile retries it and reclaims it the
        // moment it's back.
        const name = (err as Error)?.name || ''
        if (effectiveDeviceId && /OverconstrainedError|NotFoundError|NotReadableError/i.test(name)) {
          console.warn(`[MediaManager] mic device '${effectiveDeviceId}' unavailable (${name}); using OS default for this capture, keeping '${effectiveDeviceId}' pinned for retry`)
          rawStream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(undefined))
          fellBackToDefault = true
        } else {
          throw err
        }
      }
      rawTrack = rawStream.getAudioTracks()[0] ?? null
      if (!rawTrack) return null
      // Pin the device we actually got, so subsequent re-acquires request
      // exactly this one — immune to OS-default flips. An explicit
      // setAudioDevice() call flows in via `deviceId` and re-pins here too.
      // EXCEPTION: a fallback capture (the requested device threw) must NOT
      // re-pin to the default — one transient Bluetooth dropout would then
      // permanently demote the user from their chosen mic to the built-in
      // array (the Sergey QA storm). Leave the pin on the intended device so
      // recovery reclaims it.
      if (!fellBackToDefault) {
        const capturedMicId = rawTrack.getSettings().deviceId
        if (capturedMicId) this._pinnedMicDeviceId = capturedMicId
      }

      // OS-level denoiser detection. If the user already picked a
      // virtual mic from Krisp / NVIDIA Broadcast / etc., that track
      // is pre-cleaned and we would only do harm by layering DFN or
      // RNNoise on top (double NS = watery consonants + ducking
      // artefacts). Skip our cascade entirely, publish the raw
      // track. The badge will show "Krisp" / "NVIDIA Broadcast"
      // instead of "DFN" so the user knows what's running.
      const osBrand: OsDenoiserBrand = detectOsDenoiserFromTrack(rawTrack)
      if (osBrand) {
        this.destroyDenoiser()
        this._osDenoiserActive = true
        // AGC-only graph over the OS-denoised track: sendAgc is a separate
        // concern from noise suppression — without it the user arrives at
        // an unleveled loudness even though their mic is clean (invariant
        // #57). Reuses `this.denoiserHandle` for teardown discipline
        // (destroyDenoiser + the #4 _suppressTrackDeathRecovery guard).
        // Kill-switch: localStorage 'voiceAgcOverOsDenoiser'='false' restores
        // the raw OS-denoised publish.
        let publishedTrack: MediaStreamTrack = rawTrack
        const agcOn =
          typeof localStorage === 'undefined' ||
          localStorage.getItem('voiceAgcOverOsDenoiser') !== 'false'
        if (agcOn) {
          try {
            const agcHandle = await applyAgcOnly(rawTrack)
            this.denoiserHandle = agcHandle
            publishedTrack = agcHandle.track
            console.info('[MediaManager] AGC-only graph layered over OS denoiser (sendAgc leveling).')
          } catch (err) {
            console.warn('[MediaManager] AGC-only over OS denoiser failed (non-fatal); publishing OS-denoised raw:', err)
          }
        }
        const participant = room.localParticipant
        const existingPub = this.getLocalPublication(participant, 'audio')
        if (existingPub?.track instanceof LocalAudioTrack) {
          await existingPub.track.replaceTrack(publishedTrack)
        } else {
          await participant.publishTrack(publishedTrack, {
            source: Track.Source.Microphone,
          })
        }
        console.info(`[MediaManager] OS denoiser detected on mic ('${rawTrack.label}'). Skipping in-app NS.`)
        // Death watcher still tracks the OS-level rawTrack — that's the
        // one the OS revokes (USB unplug, BT dropout). agcHandle.destroy()
        // stops rawTrack too, but destroyDenoiser sets
        // _suppressTrackDeathRecovery (#4) to prevent a false recovery
        // during our own teardown.
        this.attachMicTrackDeathWatcher(rawTrack, room)
        this._consecutiveTrackDeathRecoveries = 0
        return osBrand === 'krisp' ? 'os-krisp' : 'os-nvidia'
      }
      // No OS denoiser → our cascade will own the mic. Make sure the
      // flag is reset in case a previous session left it set.
      this._osDenoiserActive = false

      // RNNoise-only cascade since DFN removal (1.1.177A). The DFN backend
      // was the previous "primary" with RNNoise as fallback, but DFN was
      // removed after repeated edge-case incidents (148A CPU overload on
      // weak machines; 174A loudness drift on strong ones). See
      // `feedback_dfn_capability_gate` memory for the full history. RNNoise
      // alone is stable, light (~1-2 ms/frame), and covers the team's
      // needs; the per-room sharedMicCapture path has an AGC-only fallback
      // (#57/#58) if RNNoise itself fails.
      // (Unused) `forceCascade` parameter is preserved to keep the
      // _reapplyFromHealthCheck signature stable; it had effect only on
      // the old DFN startup stopgap which no longer exists.
      void forceCascade
      const cascade: DenoiserBackendName[] = ['rnnoise']

      // Tear down any previous handle BEFORE building the new one —
      // keeps the order of disconnect/connect calls deterministic
      // if the user toggles fast or switches backends.
      this.destroyDenoiser()

      let handle: DenoiserHandle | null = null
      let usedBackend: DenoiserBackendName | null = null
      for (const name of cascade) {
        try {
          const backend = await loadDenoiserBackend(name)
          handle = await backend.apply(rawTrack)
          if (handle) {
            usedBackend = name
            break
          }
        } catch (err) {
          // loadDenoiserBackend itself failed (bundle / dynamic-import
          // error). Try the next cascade entry instead of bailing.
          console.error(`[MediaManager] denoiser '${name}' init threw:`, err)
        }
      }

      if (!handle || !usedBackend) {
        // Every backend in the cascade returned null OR threw. Drop
        // the raw mic we opened (don't leak the OS capture) and let
        // the caller fall back to browser-native NS.
        try { rawTrack.stop() } catch { /* noop */ }
        return null
      }

      this.denoiserHandle = handle

      const participant = room.localParticipant
      const existingPub = this.getLocalPublication(participant, 'audio')
      if (existingPub?.track instanceof LocalAudioTrack) {
        // In-place track swap — no unpublish flicker on remote peers.
        await existingPub.track.replaceTrack(handle.track)
      } else {
        await participant.publishTrack(handle.track, {
          source: Track.Source.Microphone,
        })
      }

      // OS-level mic-track death watcher. The raw mic MediaStreamTrack
      // fires 'ended' when the OS revokes it: USB unplug, Bluetooth
      // dropout, antivirus/policy block, OS sleep. Without proactive
      // recovery the user stays silent until they manually toggle
      // mute/unmute. Re-acquire through the same single-flight chain
      // — health check at 5s would catch it too but the silent window
      // is unacceptable for the "20 in a call, 2 mysteriously silent"
      // scenario.
      this.attachMicTrackDeathWatcher(rawTrack, room)
      // Successful apply on a fresh mic graph — give auto-recovery
      // its full strike budget back. Otherwise a previous burst of
      // failures stays "remembered" forever and a real OS revoke
      // later in the same session would short-circuit recovery.
      this._consecutiveTrackDeathRecoveries = 0

      // (Previously: DFN runtime degrade watcher + RNNoise startup stopgap.
      // Both removed in 1.1.177A along with DFN itself — RNNoise is C-level
      // fast and never falls behind real-time, so no degrade watcher is
      // needed; and the stopgap pattern only ever existed to mask DFN's
      // ~10-15s cold start.)

      return usedBackend
    } catch (err) {
      console.error('[MediaManager] applyDenoiserMic failed:', err)
      // Best-effort raw-track cleanup if we got that far before the throw.
      if (rawTrack) {
        try { rawTrack.stop() } catch { /* noop */ }
      }
      return null
    }
  }

  async toggleMute(room: Room): Promise<boolean> {
    const currentlyEnabled = room.localParticipant.isMicrophoneEnabled
    return this.setMicrophoneEnabled(room, !currentlyEnabled)
  }

  async setAudioDevice(room: Room, deviceId: string | null): Promise<boolean> {
    // An explicit pick is the user's sticky intent. null = they chose
    // "Default" → clear both the sticky choice and the captured pin so we
    // honour the OS default again. A concrete id is recorded in
    // applyDenoiserMic (so the join-time saved-preference path pins it too).
    if (!deviceId) {
      this._userMicDeviceId = null
      this._pinnedMicDeviceId = null
    }
    const shouldBeEnabled = room.localParticipant.isMicrophoneEnabled
    return this.setMicrophoneEnabled(room, shouldBeEnabled, { deviceId: deviceId || undefined })
  }

  async unlockAudio(room: Room): Promise<void> {
    try {
      await room.startAudio()
    } catch (err) {
      console.warn('[MediaManager] startAudio failed, will retry on next interaction:', (err as Error)?.message)
    }
    // Also unlock our custom background audio elements (autoplay policy)
    this.unlockBackgroundAudioElements()
  }

  private unlockBackgroundAudioElements(): void {
    if (typeof document === 'undefined') return
    document.querySelectorAll('audio[data-lk-background]').forEach((el) => {
      const audio = el as HTMLAudioElement
      if (audio.paused) {
        audio.play().catch(() => {})
      }
    })
  }

  // ─────────────────────────────────────────────────────────────
  //  Public API — Video
  // ─────────────────────────────────────────────────────────────

  // Track the quality preset that's actually applied to the live
  // publication. LiveKit caches the underlying MediaStreamTrack and only
  // re-uses it on subsequent enables (see "Why republish" below) — we
  // need our own marker to detect when the user picked a different
  // preset since the last publish so we can force a fresh
  // unpublish/republish cycle.
  private appliedVideoQuality: string | null = null

  // Switch the active camera (mirrors setAudioDevice for the mic).
  // Re-runs setCameraEnabled with the new deviceId, preserving the
  // current on/off state — picking a device while the camera is on
  // republishes the track on the chosen camera. (OSPROD-101)
  async setVideoDevice(room: Room, deviceId: string | null): Promise<boolean> {
    const shouldBeEnabled = room.localParticipant.isCameraEnabled
    return this.setCameraEnabled(room, shouldBeEnabled, { deviceId: deviceId || undefined })
  }

  async setCameraEnabled(
    room: Room,
    enabled: boolean,
    options?: { deviceId?: string | null },
  ): Promise<boolean> {
    // Mutex check: route ownership through the per-room MediaContext.
    // If another context already owns the camera, throw CameraBusyError
    // so the UI can prompt the user (yield camera here?) instead of
    // silently failing or producing a contested capture.
    const ctx = this.forRoom(room)
    if (!enabled) {
      this.releaseCamera(ctx)
      const res = await this.setTrackEnabled(room, 'video', false)
      // (Previously called maybeRecoverDfnAfterRelief here to promote
      // RNNoise→DFN when camera-off freed CPU. Removed in 1.1.177A with DFN.)
      try { this.forRoom(room)._onWarmSignal() } catch { /* warm re-eval is best-effort */ }
      return res
    }
    // OSPROD-145: short-circuit the camera turn-on when the site permission
    // for camera is known to be Blocked in Chrome. Without this, every
    // toggle click re-enters livekit-client's internal gUM → NotAllowedError
    // round-trip and re-spams the browser; the circuit-breaker module also
    // catches the actual gUM denials in setTrackEnabled below, but checking
    // here means a click while blocked never even reaches livekit, and the
    // toast is re-shown immediately as direct feedback.
    if (isCameraPermissionDenied()) {
      markCameraPermissionDenied('toggle')
      return this.verifyGroundTruth(room, 'video', false)
    }
    this.claimCamera(ctx)
    // Effective quality: a CPU-pressure downscale override (audio-first)
    // wins over the user's chosen preset; restored when the CPU relaxes.
    const quality = this._cpuVideoOverride ?? readVideoQuality()
    const cfg = getVideoConfig(quality)
    const captureOptions: any = { resolution: cfg.resolution }
    if (options?.deviceId) captureOptions.deviceId = options.deviceId
    const publishOptions: any = {
      videoEncoding: cfg.encoding,
      videoCodec: cfg.codec,
      // SVC scalabilityMode + the VP9→H.264 backup track are VP9-only. With the
      // 2026-06-19 H.264-default camera (videoQuality.cameraVideoCodec — H.264 is
      // HW-decoded everywhere incl. Mac, unlike VP9), LiveKit does automatic
      // simulcast and no backup is needed (H.264 is universally decodable).
      // Must mirror livekitStore/dmCallStore publishDefaults exactly so a
      // per-publish quality switch doesn't reintroduce VP9 SVC.
      ...(cfg.codec === 'vp9'
        ? { scalabilityMode: cfg.scalabilityMode, backupCodec: { codec: 'h264' as const } }
        : {}),
    }

    // Why republish: livekit-client's LocalParticipant.setCameraEnabled(true)
    // only calls track.unmute() when a publication already exists — the
    // captureOptions and publishOptions passed in are silently ignored.
    // Combined with setCameraEnabled(false) being a no-op `track.mute()`
    // for camera (LocalParticipant.ts:657-668), that means the user's
    // quality dropdown changes never reach the wire: the same encoding
    // applies for the lifetime of the publication, and toggling the
    // camera off+on does NOT re-create it.
    //
    // To make options actually take effect we explicitly unpublish the
    // existing publication when the desired preset differs from what's
    // currently live, OR when a stale publication exists with no clean
    // applied marker (e.g. first toggle after a quality change).
    const existingPub = this.getLocalPublication(room.localParticipant, 'video')
    if (existingPub?.track && this.appliedVideoQuality !== quality) {
      try {
        await room.localParticipant.unpublishTrack(existingPub.track, /* stopOnUnpublish */ true)
      } catch (err) {
        console.warn('[MediaManager] unpublish before quality switch failed:', err)
      }
    }

    // Seed the persisted background effect BEFORE deciding how to publish, so the
    // privacy path's processor already carries the saved effect. The effect IS
    // stored server-side (user.settings.backgroundEffect → cross-device), but it
    // was only re-applied when BackgroundEffectPicker MOUNTS — which often doesn't
    // happen on a fresh channel join/reload, so the camera republished with no
    // background ("фон слетает"). Seeding here ties it to the camera-publish
    // lifecycle. Once-only + only when nothing is set, so a deliberate mid-session
    // "background off" isn't clobbered.
    if (!this._bgSeededFromSettings) {
      this._bgSeededFromSettings = true
      if (this.backgroundEffect.kind === 'none') {
        try {
          const e: any = useAuthStore.getState().user?.settings?.backgroundEffect
          if (e?.kind === 'blur') this.backgroundEffect = { kind: 'blur', strength: e.strength ?? 8 }
          else if (e?.kind === 'image' && e.imageUrl) this.backgroundEffect = { kind: 'image', imageUrl: e.imageUrl }
        } catch { /* noop — never block camera-on on a settings read */ }
      }
    }

    // Seed camera frame (zoom/pan) from the user's PROFILE settings on the
    // first camera-on of the session (OSPROD-232) — camera settings are
    // profile-wide: same frame in every room, on every device, after
    // relogin. Once-only + only while still at identity, so a deliberate
    // mid-session change (or reset) isn't clobbered by a later camera-on.
    // Same lifecycle-tie rationale as the background seed above: the
    // CameraZoomPicker mount effect alone doesn't run on a fresh join.
    if (!this._frameSeededFromSettings) {
      this._frameSeededFromSettings = true
      if (this.videoZoom === 1 && this.videoPanX === 0 && this.videoPanY === 0) {
        try {
          const s: any = useAuthStore.getState().user?.settings
          const { clampZoom, clampPan } = await import('./zoomProcessor')
          const z = Number(s?.cameraZoom)
          const px = Number(s?.cameraPanX)
          const py = Number(s?.cameraPanY)
          if (Number.isFinite(z)) this.videoZoom = clampZoom(z)
          if (Number.isFinite(px)) this.videoPanX = clampPan(px)
          if (Number.isFinite(py)) this.videoPanY = clampPan(py)
        } catch { /* noop — never block camera-on on a settings read */ }
      }
    }

    // Seed camera-enhancement: PROFILE settings win (cross-device,
    // OSPROD-232); the device-local localStorage lever is the offline/legacy
    // fallback (its value is migrated up by CameraEnhancePicker's first save).
    try {
      this.videoEnhance = resolveEnhance01(useAuthStore.getState().user?.settings as Record<string, unknown> | undefined)
    } catch {
      this.videoEnhance = enhanceLevels01()
    }

    // PRIVACY (OSPROD-197, flag-gated — videoPrivacyEnabled). When an effect is
    // active, publish the REAL camera track MUTED (no frames on the wire, but
    // correct camera-resolution simulcast), attach the processor via the normal
    // single attach path, then unmute — peers NEVER receive the raw frame and the
    // sender encodings stay intact (no "encodings mismatch"/frozen video). Falls
    // back to the legacy publish if anything throws. See VOICE_CRITICAL_INVARIANTS #68.
    // PRIVACY + auto-face-center: pre-seed a tiny zoom (1.01, visually unchanged)
    // so the INITIAL processor already includes the ZoomProcessor stage. Otherwise
    // the deferred setAutoFaceCenter() would call setVideoZoom(1.01) →
    // applyVideoEffectsToRoom → stopProcessor, which (livekit) briefly re-sets the
    // RAW track on the sender = a raw flash on camera-on for face-center users.
    // With the stage already present, setAutoFaceCenter only enables the tracker.
    // Auto-framing on (re)join: background wins (Rule 4). computeJoinState
    // yields the EFFECTIVE state from the saved preference + whether a
    // background is active, and we push it back into voiceStore so the
    // button reflects reality. This replaces the old "force ON every join"
    // override that ignored a user's saved-off (see livekitStore/dmCallStore
    // connect — both removed).
    let joinFaceCenter = false
    try {
      const vs = useVoiceStore.getState()
      const joined = computeJoinState(vs.autoFaceCenterPref, this.backgroundEffect.kind !== 'none')
      joinFaceCenter = joined.effective
      useVoiceStore.setState({
        autoFaceCenter: joined.effective,
        autoFaceCenterSuppressedByBg: joined.suppressedByBg,
      })
    } catch { /* voiceStore not initialised in tests / SSR */ }
    if (videoPrivacyEnabled() && this.hasAnyVideoEffect() &&
        this.videoZoom === 1 && this.videoPanX === 0 && this.videoPanY === 0) {
      if (joinFaceCenter) this.videoZoom = 1.01
    }
    // OSPROD-271: BOUNDED enable with ONE retry. A camera-enable attempt that
    // stalls on a pending await (gUM, processor init `video.play()`, LiveKit
    // publish — observed on prod while a call recording was active: the tile
    // spinner never resolved and only "Stop Recording" unstuck it) now times
    // out after CAMERA_ENABLE_TIMEOUT_MS, cleans up any half publication,
    // retries once with a fresh generation, and if the retry stalls too it
    // throws CameraEnableTimeoutError so the UI shows a clear error instead
    // of spinning forever. Telemetry: voice_diag {type:'camera-enable-stall'}.
    const privacyWanted = videoPrivacyEnabled() && this.hasAnyVideoEffect()
    let lastTimeout: CameraEnableTimeoutError | null = null
    for (let attempt = 1; attempt <= 2; attempt++) {
      // New generation per attempt: a timed-out attempt's promise cannot be
      // cancelled, so publishCameraWithEffect re-checks this before it
      // publishes — a LATE completion must not add a second camera track on
      // top of the retry's publication.
      const gen = ++this._camEnableGeneration
      let publishedPrivately = false
      if (privacyWanted) {
        try {
          await withCameraEnableTimeout(
            'private-publish',
            this.publishCameraWithEffect(room, captureOptions, publishOptions, gen),
          )
          this.appliedVideoQuality = quality
          publishedPrivately = true
        } catch (err) {
          if (err instanceof CameraEnableTimeoutError) {
            this.noteCameraEnableStall(err.stage, `attempt=${attempt} recording-safe cleanup`)
          }
          console.warn('[MediaManager] private camera publish failed; using legacy publish:', err)
          const half = this.getLocalPublication(room.localParticipant, 'video')
          if (half?.track) { try { await room.localParticipant.unpublishTrack(half.track, true) } catch { /* noop */ } }
          publishedPrivately = false
        }
      }

      if (!publishedPrivately) {
        try {
          await withCameraEnableTimeout(
            'livekit-enable',
            Promise.resolve(room.localParticipant.setCameraEnabled(true, captureOptions, publishOptions)),
          )
          this.appliedVideoQuality = quality
        } catch (err) {
          if (err instanceof CameraEnableTimeoutError) {
            this.noteCameraEnableStall(err.stage, `attempt=${attempt}`)
            lastTimeout = err
            const half = this.getLocalPublication(room.localParticipant, 'video')
            if (half?.track) { try { await room.localParticipant.unpublishTrack(half.track, true) } catch { /* noop */ } }
            continue
          }
          console.error('[MediaManager] setCameraEnabled with explicit options failed:', err)
          // Fall back to plain setTrackEnabled so we at least try the room
          // defaults rather than leaving the user without video. Bounded the
          // same way — a stuck fallback must not hang the toggle either.
          try {
            return await withCameraEnableTimeout(
              'fallback-enable',
              this.setTrackEnabled(room, 'video', true, captureOptions),
            )
          } catch (fbErr) {
            if (fbErr instanceof CameraEnableTimeoutError) {
              this.noteCameraEnableStall(fbErr.stage, `attempt=${attempt}`)
              lastTimeout = fbErr
              continue
            }
            throw fbErr
          }
        }
        // Re-apply zoom / pan / background after camera turn-on. No-op when nothing
        // is configured. (Legacy path: the raw camera is briefly on the wire before
        // the processor attaches — the accepted pre-privacy behaviour, flag off.)
        if (this.hasAnyVideoEffect()) {
          this.applyVideoEffectsToRoom(room).catch(() => {})
        }
      }
      lastTimeout = null
      break
    }
    if (lastTimeout) {
      // Both bounded attempts stalled. Resolve LOUDLY: livekitStore.toggleVideo
      // catches this, reverts the optimistic state (spinner stops) and shows a
      // clear "camera didn't start" toast. Previously this path never settled.
      throw lastTimeout
    }
    // Re-engage / disengage Center-Stage auto-framing to match the join
    // state computed above. joinFaceCenter already folds in the saved
    // preference AND background suppression, so a user who turned it off
    // (or who has a background on) does NOT get it forced back on. Defer to
    // a microtask so the publish state from this call settles before the
    // tracker reads combinedProcessor's source video.
    if (joinFaceCenter) {
      Promise.resolve().then(() => this.setAutoFaceCenter(room, true)).catch(() => {})
    } else if (this.autoFaceCenter) {
      // mediaManager still thinks framing is on from a prior room — turn it
      // off so a saved-off / background-suppressed join doesn't keep tracking.
      Promise.resolve().then(() => this.setAutoFaceCenter(room, false)).catch(() => {})
    }
    try { this.forRoom(room)._onWarmSignal() } catch { /* warm re-eval is best-effort */ }
    return this.verifyGroundTruth(room, 'video', true)
  }

  /**
   * PRIVACY (OSPROD-197): publish the camera so peers NEVER see a raw frame —
   * WITHOUT muting. A muted camera track yields 0x0 frames, which starves the
   * matting processor (RVM gets a {1,3,0,0} input and dies) — that was the
   * camera-won't-start bug. Instead:
   *   1. createLocalVideoTrack → a LIVE but UNPUBLISHED camera track (real frames,
   *      real dimensions) that is never attached to any sender.
   *   2. setProcessor on it → the processor consumes the live camera and exposes
   *      its composited BACKGROUND canvas as camTrack.mediaStreamTrack. The raw
   *      camera is never on the wire.
   *   3. publishTrack the PROCESSED (canvas) track SINGLE-LAYER — a canvas
   *      captureStream can't simulcast (the browser allocates fewer encodings than
   *      configured → setPublishingLayers mismatch → SFU stops forwarding → frozen
   *      video for peers), so we publish one layer.
   * Frames are background-only until the matte arrives (BackgroundProcessor
   * privacy), never raw. Lets failures bubble so setCameraEnabled can fall back to
   * the legacy publish. See VOICE_CRITICAL_INVARIANTS #68.
   */
  private async publishCameraWithEffect(
    room: Room,
    captureOptions: any,
    publishOptions: any,
    // OSPROD-271: generation snapshot from setCameraEnabled. When this
    // attempt was timed out by the watchdog and a retry started (generation
    // bumped), a LATE completion of this call must abort instead of
    // publishing a second camera track. `undefined` (internal callers)
    // skips the check.
    enableGeneration?: number,
  ): Promise<void> {
    // OSPROD-271: stage stopwatch. Every await below is a candidate for the
    // 15-20s enable seen on prod; the existing telemetry couldn't tell them
    // apart. `flush` also runs on the failure paths, so a LATE completion of
    // an attempt the watchdog already abandoned still reports which await it
    // was sitting in — that is precisely the number we lack today.
    const t0 = performance.now()
    const marks: string[] = []
    let lastMark = t0
    const mark = (name: string) => {
      const now = performance.now()
      marks.push(`${name}=${Math.round(now - lastMark)}`)
      lastMark = now
    }
    const flush = (outcome: string) => {
      const total = performance.now() - t0
      if (total < CAMERA_ENABLE_SLOW_MS) return
      this.noteCameraEnableTiming(`${marks.join('|')}|outcome=${outcome}`, total)
    }

    const { createLocalVideoTrack } = await import('livekit-client')
    mark('import-lk')
    // Drop any existing camera publication so we don't publish a SECOND track with
    // the same source (livekit warns + the encodings recompute breaks).
    const existing = this.getLocalPublication(room.localParticipant, 'video')
    if (existing?.track) {
      try { await room.localParticipant.unpublishTrack(existing.track, true) } catch { /* noop */ }
      mark('unpublish-existing')
    }
    const camTrack = await createLocalVideoTrack(captureOptions)
    mark('gum')
    let combined: import('./zoomProcessor').CombinedProcessor | null = null
    try {
      const { CombinedProcessor } = await import('./zoomProcessor')
      mark('import-zoom')
      combined = new CombinedProcessor({
        zoom: this.videoZoom,
        panX: this.videoPanX,
        panY: this.videoPanY,
        bg: this.backgroundEffect,
        engine: backgroundEngine(),
        privacy: true,
        enhance: this.videoEnhance,
        loadBgModule: () => this.loadProcessors(),
      })
      // Attach BEFORE publish: processor reads the live (unpublished) camera; raw
      // never reaches a sender. camTrack.mediaStreamTrack now yields the canvas.
      if (typeof (camTrack as any).setProcessor === 'function') {
        await (camTrack as any).setProcessor(combined as any)
        // Fattest await on the path: hidden <video> + playBounded, then the
        // lazy background stage pulls the ORT WASM + RVM model over HTTP.
        mark('setProcessor')
      }
      // OSPROD-271: if the enable watchdog already timed this attempt out and
      // a fresh attempt is running (generation moved on), abort BEFORE the
      // publish — a late second camera publication breaks the SFU encodings.
      if (enableGeneration !== undefined && enableGeneration !== this._camEnableGeneration) {
        // A superseded attempt is the most valuable timing sample there is:
        // it is the one the watchdog gave up on, and it finally tells us how
        // long the blocking stage actually ran.
        flush('superseded')
        throw new Error('camera enable attempt superseded (watchdog retry in progress)')
      }
      this.combinedProcessor = combined
      // Publish the PROCESSED (canvas) track, SINGLE layer (canvas can't simulcast).
      await room.localParticipant.publishTrack(camTrack as any, {
        ...publishOptions,
        simulcast: false,
        scalabilityMode: undefined,
        backupCodec: undefined,
      })
      // AddTrack round-trip to the SFU. livekit-client's own timeout here is
      // 10s — the same budget as our watchdog — so a signalling stall and a
      // local stall are indistinguishable until this number exists.
      mark('publish')
      flush('ok')
    } catch (err) {
      flush('error')
      if (combined) { try { await combined.destroy() } catch { /* noop */ } }
      // OSPROD-271: only clear the singleton if it is OURS — a superseded
      // (watchdog-retried) attempt must not null out the retry's processor.
      if (this.combinedProcessor === combined) this.combinedProcessor = null
      try { await (camTrack as any).stop?.() } catch { /* noop */ }
      throw err
    }
    this.watchRx2goFallback(room, combined)
    // Single-layer canvas track just went on the wire — if the room is already
    // busy it must publish at 180p/250k (the subscribe-side cap can't step a
    // single layer down). Deferred a microtask so the sender's params exist.
    // The livekitStore latch also re-applies this on the next membership event.
    Promise.resolve().then(() => {
      try { this.applyBusyRoomPublishCap(room, useLivekitStore.getState().bigRoomForcedLow) } catch { /* best-effort */ }
    })
  }

  /**
   * Busy-room PUBLISHER cap for single-layer canvas tracks. A processed camera
   * (virtual background / blur / zoom / face-center) publishes ONE flat canvas
   * layer (`simulcast:false`, no SVC — invariant #68), so the subscribe-side
   * 180p cap has nothing lower to forward and every viewer pulls the full
   * preset bitrate. On a busy call (`bigRoomForcedLow`) we shrink the publisher
   * itself via `RTCRtpSender.setParameters` — non-disruptive (no republish / no
   * SDP renegotiation, so none of the republish-storm risks in #37/#41).
   *
   * Gated on `combinedProcessor != null` AND a single sender encoding, so plain
   * simulcast / VP9-SVC cameras (which the subscribe cap already handles) are
   * never touched. Idempotent: skips the setParameters when already at target.
   * Driven from livekitStore.applyBigRoomCap (every membership/publish event)
   * plus the post-publish call above.
   */
  applyBusyRoomPublishCap(room: Room, bigRoomForcedLow: boolean): void {
    const isCanvas = this.liveProcessor() != null
    // ENGAGE only on a processed/canvas publish; always allow RESTORE if we
    // previously capped (so a track can't get stuck low after bg-off).
    if (!isCanvas && !this._busyRoomCapped) return
    const pub = this.getLocalPublication(room.localParticipant, 'video')
    const track = pub?.track as LocalVideoTrack | undefined
    const sender = (track as unknown as { sender?: RTCRtpSender } | undefined)?.sender
    if (!sender) return
    let params: RTCRtpSendParameters
    try { params = sender.getParameters() } catch { return }
    const encs = params.encodings
    if (!encs || encs.length !== 1) return // simulcast / SVC: subscribe-side cap owns it
    // Don't newly cap a non-canvas single-encoding (VP9-SVC) camera — the SFU
    // can extract layers from it, so the subscribe-side cap already serves each
    // viewer the right one and a hard publisher cap would kill fullscreen sharpness.
    if (bigRoomForcedLow && !isCanvas) return
    const preset = getVideoConfig(this._cpuVideoOverride ?? readVideoQuality())
    const want = busyRoomPublishSenderParams({ bigRoomForcedLow, preset })
    if (encs[0].maxBitrate === want.maxBitrate && encs[0].scaleResolutionDownBy === want.scaleResolutionDownBy) {
      this._busyRoomCapped = bigRoomForcedLow // keep flag in sync even when already at target
      return // already at target — no renegotiation
    }
    encs[0].maxBitrate = want.maxBitrate
    encs[0].scaleResolutionDownBy = want.scaleResolutionDownBy
    this._busyRoomCapped = bigRoomForcedLow
    sender.setParameters(params)
      .then(() => console.info(`[MediaManager] busy-room publish cap ${bigRoomForcedLow ? 'ENGAGED' : 'released'} on canvas track — ${want.maxBitrate} bps, ${want.scaleResolutionDownBy}x`))
      .catch((e) => console.warn('[MediaManager] busy-room publish cap setParameters failed:', e))
  }

  /**
   * Watch the rx2go background engine for a REAL failure (repeated inference
   * errors — not slowness) and, when it happens, force the stock livekit processor
   * for THIS SESSION ONLY (in-memory) and re-apply. Poll-based; self-clears when
   * the chain is replaced or torn down. Shared by publishCameraWithEffect and
   * applyVideoEffectsToRoom.
   */
  private watchRx2goFallback(
    room: Room,
    combined: import('./zoomProcessor').CombinedProcessor,
  ): void {
    if (this.combinedProcessor !== combined || backgroundEngine() !== 'rx2go') return
    const timer = window.setInterval(() => {
      if (this.combinedProcessor !== combined) { window.clearInterval(timer); return }
      if (combined.needsStockFallback()) {
        window.clearInterval(timer)
        console.warn('[MediaManager] rx2go bg engine failed — falling back to stock for this session')
        setBackgroundEngineSessionOverride('livekit')
        if (videoPrivacyEnabled()) {
          // OSPROD-197 regression guard (2026-07-07): the in-place re-apply
          // below goes through livekit stopProcessor → setProcessor, which
          // puts the RAW camera on the sender for the entire stock
          // (MediaPipe) init — with privacy ON the user's real surroundings
          // were streamed for seconds every time the rx2go engine failed
          // (prod 2026-07-06/07: dead RVM made this fire on EVERY camera-on).
          // Swap via unpublish → privacy republish instead: the stock
          // processor attaches BEFORE publish, peers see the tile drop for
          // ~1-2 s, never the raw room.
          this.privacySafeStockSwap(room).catch((err) =>
            console.warn('[MediaManager] privacy-safe stock swap failed:', err))
        } else {
          this.applyVideoEffectsToRoom(room).catch(() => {})
        }
      }
    }, 2000)
  }

  /**
   * Replace the live camera publication with a STOCK-engine publish without
   * ever exposing the raw camera (OSPROD-197). Used by watchRx2goFallback
   * when the rx2go bg engine dies mid-call and privacy is ON: unpublish the
   * current (still processed — bg-only at worst) canvas track, then run the
   * normal camera-enable pipeline. With the session override already set to
   * 'livekit' it builds the stock processor and attaches it to a live but
   * UNPUBLISHED camera before publishTrack — the raw frame never reaches a
   * sender (invariant #68).
   */
  private async privacySafeStockSwap(room: Room): Promise<void> {
    const pub = this.getLocalPublication(room.localParticipant, 'video')
    const track = pub?.track
    if (!track) return // camera already off — the next enable picks up the stock engine
    try {
      await room.localParticipant.unpublishTrack(track as any, true)
    } catch (err) {
      console.warn('[MediaManager] privacy-safe swap unpublish failed (continuing):', err)
    }
    // Force a full re-publish (not livekit's unmute fast-path) so the new
    // processor chain is attached pre-publish.
    this.appliedVideoQuality = null
    await this.setCameraEnabled(room, true)
  }

  async toggleVideo(room: Room): Promise<boolean> {
    const currentlyEnabled = room.localParticipant.isCameraEnabled
    return this.setCameraEnabled(room, !currentlyEnabled)
  }

  // Re-publish the camera with the current quality preset. Used by the
  // quality picker — fully unpublishes the existing camera track so the
  // next publish picks up the new resolution/bitrate.
  async restartCameraForQuality(room: Room): Promise<boolean> {
    if (!room.localParticipant.isCameraEnabled) {
      // Camera is off; mark current quality as "unapplied" so the next
      // enable will treat it as a fresh publish (same effect as a real
      // restart, no flicker).
      this.appliedVideoQuality = null
      return false
    }
    // Force an unpublish so setCameraEnabled below cannot fast-path
    // through livekit's unmute branch and skip our captureOptions /
    // publishOptions.
    const existingPub = this.getLocalPublication(room.localParticipant, 'video')
    if (existingPub?.track) {
      try {
        await room.localParticipant.unpublishTrack(existingPub.track, true)
      } catch (err) {
        console.warn('[MediaManager] restartCameraForQuality unpublish failed:', err)
      }
    }
    this.appliedVideoQuality = null
    const ok = await this.setCameraEnabled(room, true)
    // Re-apply zoom / pan / blur / virtual background after the new
    // track is published.
    if (ok && this.hasAnyVideoEffect()) {
      await this.applyVideoEffectsToRoom(room).catch(() => {})
    }
    return ok
  }

  // ─────────────────────────────────────────────────────────────
  //  Public API — Background Effects (blur / virtual background)
  //
  // Powered by `@livekit/track-processors`. The processor is attached
  // to the local camera track — peers receive the already-processed
  // video. Toggling between effects is in-place: no track restart, no
  // LiveKit reconnect.
  // ─────────────────────────────────────────────────────────────

  getBackgroundEffect(): BackgroundEffect {
    return this.backgroundEffect
  }

  async setBackgroundEffect(room: Room | null, effect: BackgroundEffect): Promise<void> {
    this.backgroundEffect = effect
    if (!room) return
    // Let failures bubble so the picker can show a toast — the previous
    // signature swallowed everything and the user had no clue why their
    // blur button did nothing.
    await this.applyVideoEffectsToRoom(room)
  }

  /** Currently applied zoom factor (1 = identity). */
  getVideoZoom(): number {
    return this.videoZoom
  }

  /** Currently applied pan offsets. */
  getVideoPan(): { x: number; y: number } {
    return { x: this.videoPanX, y: this.videoPanY }
  }

  /**
   * Re-frame the local camera before publish so peers see the user at
   * the requested zoom + pan. When a CombinedProcessor is already
   * running we ALWAYS tweak it live — the zoom stage is unconditionally
   * part of the chain (OSPROD-232), so no slider value can require a
   * rebuild. The old identity-crossing rebuild meant dragging across
   * 1.00 destroyed + rebuilt the whole chain (RVM model reload, republish,
   * raw flash) TWICE per pass — QA measured "any zoom change multiplies
   * latency". Returning to full identity now keeps the (identity-drawing)
   * processor alive until the next camera off/on — a slightly lower-quality
   * single-layer publish in exchange for zero mid-call rebuilds.
   */
  /** The live CombinedProcessor, or null. Clears a STALE reference: LiveKit
   *  destroys the attached processor when the camera track stops (camera-off,
   *  quality restart), and a destroyed processor would otherwise swallow live
   *  zoom/pan updates silently (OSPROD-232). */
  private liveProcessor(): import('./zoomProcessor').CombinedProcessor | null {
    if (this.combinedProcessor?.isDestroyed()) this.combinedProcessor = null
    return this.combinedProcessor
  }

  async setVideoZoom(room: Room | null, zoom: number): Promise<void> {
    const { clampZoom } = await import('./zoomProcessor')
    const next = clampZoom(zoom)
    this.videoZoom = next
    const live = this.liveProcessor()
    if (live) {
      live.setZoom(next)
      return
    }
    if (!room) return
    // No processor yet: build the chain only when something is non-identity —
    // zoom=1 with no other effect means nothing to attach.
    if (!this.hasAnyVideoEffect()) return
    await this.applyVideoEffectsToRoom(room)
  }

  async setVideoPan(room: Room | null, panX: number, panY: number): Promise<void> {
    const { clampPan } = await import('./zoomProcessor')
    const nextX = clampPan(panX)
    const nextY = clampPan(panY)
    this.videoPanX = nextX
    this.videoPanY = nextY
    const live = this.liveProcessor()
    if (live) {
      live.setPan(nextX, nextY)
      return
    }
    if (!room) return
    if (!this.hasAnyVideoEffect()) return
    await this.applyVideoEffectsToRoom(room)
  }

  /** Currently applied camera-enhancement levels (each 0..1). */
  getVideoEnhance(): EnhanceLevels {
    return this.videoEnhance
  }

  /**
   * Set the camera-enhancement levels (smooth/lowlight/vibrance, each 0..1).
   * When a CombinedProcessor is live and the on/off state doesn't flip, update
   * the shader uniforms live (no track restart). Crossing the all-zero ↔
   * any-nonzero boundary changes whether the enhance stage exists at all →
   * rebuild via applyVideoEffectsToRoom. (OSPROD-109 v2)
   */
  async setEnhanceLevels(room: Room | null, levels: EnhanceLevels): Promise<void> {
    const clamp = (v: number) => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0))
    const next: EnhanceLevels = { smooth: clamp(levels.smooth), lowlight: clamp(levels.lowlight), vibrance: clamp(levels.vibrance) }
    this.videoEnhance = next
    const live = this.liveProcessor()
    if (live) {
      // Live-update whenever possible (OSPROD-232): the stage exists → tune
      // uniforms; turning OFF with the stage attached → set zeros and KEEP the
      // stage (visually identity, one avoided rebuild). Only "turning ON while
      // the stage was never built" still needs a rebuild — the stage is
      // deliberately not attached at all-zero levels (OSPROD-109: zero cost
      // for non-users).
      if (live.hasEnhanceStage() || !anyEnhanceOn(next)) {
        live.setEnhanceLevels(next)
        return
      }
    }
    if (!room) return
    if (!this.hasAnyVideoEffect()) return
    await this.applyVideoEffectsToRoom(room)
  }

  /** Currently applied auto-framing state. */
  getAutoFaceCenter(): boolean {
    return this.autoFaceCenter
  }

  /**
   * Toggle Apple-Center-Stage-style auto-framing. When enabled, the
   * BlazeFace-backed face tracker (services/faceTracker.ts) detects
   * the user's face on the RAW camera feed (the hidden <video> inside
   * the ZoomProcessor pipeline) and drives `combinedProcessor.setTarget`
   * (via FaceTracker's onTarget hook) so the ZoomProcessor spring eases
   * toward the composed {zoom,panX,panY} each frame. Receivers see the
   * cropped feed natively — no client-side ML on their end.
   *
   * Mutex with manual zoom/pan: enabling face center takes over both
   * controls; disabling resets to identity so the next manual nudge
   * starts from a known baseline.
   */
  async setAutoFaceCenter(
    room: Room | null,
    enabled: boolean,
    opts?: { restoreFrame?: boolean },
  ): Promise<void> {
    // No short-circuit on `=== enabled` — same-state calls are how the
    // setCameraEnabled hook re-engages the tracker when the user
    // toggles camera off-then-on with the preference still set.
    const wasOn = this.autoFaceCenter
    this.autoFaceCenter = enabled
    // Lazy-load the tracker only when actually used; never bundle
    // BlazeFace + MediaPipe Tasks Vision wasm for users who don't
    // enable it.
    const { faceTracker } = await import('./faceTracker')
    if (!enabled) {
      faceTracker.disable()
      // OSPROD-232: restore the user's SAVED manual frame instead of hard
      // identity. The old reset-to-(1,0,0) was the "my zoom resets when I
      // toggle the background" bug: background-on suppresses face-center
      // (voiceStore.syncBackgroundCoupling → setAutoFaceCenter(false)) and
      // the reset wiped the user's manual zoom/pan along with it. Skip the
      // restore entirely when (a) the caller immediately applies its own
      // frame (CameraZoomPicker manual adjust passes restoreFrame:false —
      // this async body can land AFTER the slider's setVideoZoom and must
      // not overwrite it), or (b) the tracker was never on (nothing to undo).
      if (opts?.restoreFrame === false || !wasOn) return
      let zoom = 1
      let panX = 0
      let panY = 0
      try {
        const s: any = useAuthStore.getState().user?.settings
        const { clampZoom, clampPan } = await import('./zoomProcessor')
        const z = Number(s?.cameraZoom)
        const px = Number(s?.cameraPanX)
        const py = Number(s?.cameraPanY)
        if (Number.isFinite(z)) zoom = clampZoom(z)
        if (Number.isFinite(px)) panX = clampPan(px)
        if (Number.isFinite(py)) panY = clampPan(py)
      } catch { /* no saved frame — ease to identity */ }
      // Ease via the spring instead of snapping — calling setVideoZoom here
      // used to tear the processor down on identity, which broke the local
      // tile's <video> binding mid-frame (preview disappeared while peers
      // were fine). setTarget keeps the processor alive through the ~250 ms
      // ease. Subsequent manual zoom (CameraZoomPicker.setZoom) snaps both
      // current AND target via ZoomProcessor.setZoom — the user stays free
      // to take over.
      this.combinedProcessor?.setTarget(zoom, panX, panY)
      this.videoZoom = zoom
      this.videoPanX = panX
      this.videoPanY = panY
      return
    }
    // Force the combined processor to exist with a non-identity zoom
    // so we have a ZoomProcessor sourceVideo to read frames from.
    // 1.01 is visually unchanged but keeps the processor in the
    // non-identity branch where setZoom/setPan stay cheap (no
    // applyVideoEffectsToRoom rebuild on every detection).
    if (this.videoZoom === 1 && this.videoPanX === 0 && this.videoPanY === 0) {
      if (room) {
        await this.setVideoZoom(room, 1.01)
      }
    }
    const sourceVideo = this.combinedProcessor?.getSourceVideo() ?? null
    if (!sourceVideo) {
      // Combined processor didn't materialise (camera off, applyVideoEffects
      // failed, etc). Bail and unset the flag so the UI reflects reality.
      console.warn('[mediaManager] setAutoFaceCenter: no source video available; aborting')
      this.autoFaceCenter = false
      return
    }
    faceTracker.enable(sourceVideo, {
      // Informational only — preserved for any UI subscribers that listen
      // to raw framing data. Crop composition now lives in faceComposition.ts
      // and is called inside FaceTracker before emitting onTarget.
      onFraming: () => { /* no-op; composition happens in FaceTracker */ },
      onTarget: (target) => {
        // FaceTracker has already applied the 1€ filter and run faceComposition,
        // so {zoom, panX, panY} is ready to feed into the spring API directly.
        // ZoomProcessor.setTarget stores the goal state; its per-frame draw()
        // loop eases current → target at ~60 fps regardless of detection cadence.
        if (!this.autoFaceCenter || !this.combinedProcessor) return
        this.combinedProcessor.setTarget(target.zoom, target.panX, target.panY)
      },
    }).catch((err) => {
      console.error('[mediaManager] faceTracker.enable failed:', err)
      this.autoFaceCenter = false
    })
  }

  private hasAnyVideoEffect(): boolean {
    return (
      this.videoZoom !== 1 ||
      this.videoPanX !== 0 ||
      this.videoPanY !== 0 ||
      this.backgroundEffect.kind !== 'none' ||
      anyEnhanceOn(this.videoEnhance)
    )
  }

  private async loadProcessors(): Promise<any> {
    if (!this.processorsModulePromise) {
      // Plain dynamic import. The previous implementation went through
      // a `new Function('p', 'return import(p)')` indirection to "hide"
      // the spec from Vite's static dep scanner so unit tests wouldn't
      // need the package installed. The cost was that the browser was
      // also asked to resolve a bare specifier at runtime — and the
      // browser can't, only Vite can — so the import always rejected
      // with "Failed to resolve module specifier '@livekit/track-processors'"
      // and background blur silently no-op'd. A direct `import()` lets
      // Vite pre-bundle the dep into a chunk that resolves correctly,
      // and tests that mock `mediaManager` still don't pull it in.
      this.processorsModulePromise = import('@livekit/track-processors').catch((err) => {
        // Reset the cache so a later retry can succeed (e.g. after the
        // package is installed via a CI cache miss).
        this.processorsModulePromise = null
        throw err
      })
    }
    return this.processorsModulePromise
  }

  /**
   * Apply the user's current zoom / pan / background-effect state to
   * the local camera by attaching a single chained `CombinedProcessor`
   * — peers see ZOOM + BLUR composed (no longer mutually exclusive).
   * Resets the published track to raw camera when no effect is active.
   */
  private async applyVideoEffectsToRoom(room: Room): Promise<void> {
    const pub = this.getLocalPublication(room.localParticipant, 'video')
    const track = pub?.track as LocalVideoTrack | undefined
    if (!track) return // camera not on yet — effect will apply on next publish

    // NOTE (OSPROD-197): this MID-CALL re-apply path goes through livekit's
    // setProcessor, which briefly re-sets the RAW track on the sender between
    // stopping the old processor and attaching the new one (internalStopProcessor
    // → setMediaStreamTrack(raw)). We do NOT mute around it: a muted camera track
    // yields 0x0 frames and starves the matting processor. The initial camera-on
    // privacy path avoids this entirely (publishCameraWithEffect attaches before
    // publish), and face-center pre-seeds the zoom stage so it doesn't trigger a
    // rebuild here. A mid-call effect CHANGE (blur↔image, manual zoom toggle) can
    // still briefly show raw to peers — accepted: the user is already visible.

    // Always start from a clean slate so re-applying doesn't pile up processors.
    try {
      if (typeof (track as any).stopProcessor === 'function') {
        await (track as any).stopProcessor()
      }
    } catch {}
    if (this.combinedProcessor) {
      try { await this.combinedProcessor.destroy() } catch {}
      this.combinedProcessor = null
    }

    if (!this.hasAnyVideoEffect()) return

    let combined: import('./zoomProcessor').CombinedProcessor | null = null
    try {
      const { CombinedProcessor } = await import('./zoomProcessor')
      combined = new CombinedProcessor({
        zoom: this.videoZoom,
        panX: this.videoPanX,
        panY: this.videoPanY,
        bg: this.backgroundEffect,
        engine: backgroundEngine(),
        privacy: videoPrivacyEnabled(),
        enhance: this.videoEnhance,
        loadBgModule: () => this.loadProcessors(),
      })
      this.combinedProcessor = combined
      if (typeof (track as any).setProcessor === 'function') {
        await (track as any).setProcessor(combined as any)
      }
      this.watchRx2goFallback(room, combined)
    } catch (err) {
      console.warn('[MediaManager] combined processor failed:', err)
      // Tear down anything we partially built so the next attempt starts
      // clean and the LocalVideoTrack falls back to raw camera.
      if (combined) {
        try { await combined.destroy() } catch {}
      }
      this.combinedProcessor = null
      throw new Error('Failed to apply your video effects')
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  Public API — Screen Share
  // ─────────────────────────────────────────────────────────────

  async setScreenShareEnabled(
    room: Room,
    enabled: boolean,
    options?: { audio?: boolean },
  ): Promise<boolean> {
    const ctx = this.forRoom(room)
    if (!enabled) {
      const sidBeforeDisable = activeScreenShareSid ||
        room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.trackSid || ''
      this.releaseScreen(ctx)
      const result = await this.setTrackEnabled(room, 'screen', false, options)
      // Emit screen-share-stopped — closes overlay window in VoiceChannelControls.
      try {
        activeScreenShareSid = ''
        emitScreenShareStopped({ trackSid: sidBeforeDisable })
      } catch (err) {
        console.warn('[mediaManager] failed to emit screen-share-stopped', err)
      }
      return result
    }
    this.claimScreen(ctx)
    const ok = await this.setTrackEnabled(room, 'screen', true, options)
    // If the share never actually started (user cancelled the picker, or
    // the enable failed), drop the claim so a later attempt — or another
    // MediaContext — isn't falsely told "screen share is in use". (OSPROD-97)
    if (!ok) this.releaseScreen(ctx)
    // Emit screen-share-started event — opens overlay window in VoiceChannelControls.
    // Done lazily after publish success so we don't fire on failed attempts.
    if (ok) {
      try {
        const pub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
        const sid = pub?.trackSid || ''
        activeScreenShareSid = sid
        const settings = (pub?.track?.mediaStreamTrack as MediaStreamTrack | undefined)?.getSettings?.() as Record<string, unknown>
        emitScreenShareStarted({
          trackSid: sid,
          bounds: {
            x: 0,
            y: 0,
            width: typeof settings?.width === 'number' ? settings.width : (typeof window !== 'undefined' ? window.screen.width : 1920),
            height: typeof settings?.height === 'number' ? settings.height : (typeof window !== 'undefined' ? window.screen.height : 1080),
            displayId: typeof settings?.displaySurface === 'string' || typeof settings?.displaySurface === 'number'
              ? settings.displaySurface
              : typeof settings?.deviceId === 'string' || typeof settings?.deviceId === 'number'
                ? settings.deviceId
                : undefined,
          },
        })
      } catch (err) {
        console.warn('[mediaManager] failed to emit screen-share-started', err)
      }
    }
    return ok
  }

  async toggleScreenShare(room: Room): Promise<boolean> {
    const currentlyEnabled = room.localParticipant.isScreenShareEnabled
    return this.setScreenShareEnabled(room, !currentlyEnabled, { audio: true })
  }

  // ─────────────────────────────────────────────────────────────
  //  Generic track enable/disable with retry + verification
  // ─────────────────────────────────────────────────────────────

  async setTrackEnabled(
    room: Room,
    kind: MediaKind,
    enabled: boolean,
    options?: Record<string, unknown>,
  ): Promise<boolean> {
    const participant = room.localParticipant

    // Preemptive zombie cleanup: if we're enabling but the existing
    // publication has an ended underlying MediaStreamTrack, LiveKit's
    // setEnabled(true) just flips the flag without republishing — peers
    // would never hear/see us. Drop the dead pub first so setEnabled
    // takes the create-and-publish path.
    if (enabled) {
      const existingPub = this.getLocalPublication(participant, kind)
      const existingTrack = existingPub?.track?.mediaStreamTrack
      if (existingPub?.track && (!existingTrack || existingTrack.readyState === 'ended')) {
        try {
          await participant.unpublishTrack(existingPub.track, true)
        } catch (err) {
          console.warn(`[MediaManager] preemptive unpublish failed for ${kind}:`, err)
        }
      }
    }

    // Map kind to LiveKit method
    const setEnabled = async () => {
      if (kind === 'audio') {
        await participant.setMicrophoneEnabled(enabled, options as any)
      } else if (kind === 'video') {
        await participant.setCameraEnabled(enabled, options as any)
      } else {
        await participant.setScreenShareEnabled(enabled, options as any)
      }
    }

    try {
      await withRetry(
        setEnabled,
        { attempt: 0, maxAttempts: this.opts.maxRetries, baseDelay: this.opts.baseRetryDelayMs },
        (err, attempt) => {
          console.warn(
            `[MediaManager] setTrackEnabled(${kind}, ${enabled}) attempt ${attempt + 1} failed:`,
            (err as Error)?.message || err,
          )
        },
      )
    } catch (err) {
      // User cancelled the OS prompt / screen-picker (or denied
      // permission): don't fall back to getUserMedia — that just
      // re-prompts. Treat it as a calm no-op, not an error. (OSPROD-97)
      if (isUserCancellation(err)) {
        // OSPROD-145: NotAllowedError on a video request is the
        // "site permission = Block" case in Chrome — sticky until the
        // user changes the site setting (no prompt is ever shown by
        // the browser). Trip the circuit breaker so subsequent UI
        // mounts (VideoDeviceSelector / Settings modal / pre-join
        // device check) short-circuit instead of joining the retry
        // storm. AbortError (user clicked Cancel on an actual prompt)
        // is NOT sticky and is left alone.
        if (kind === 'video' && (err as Error)?.name === 'NotAllowedError') {
          markCameraPermissionDenied('gum')
        }
        console.info(`[MediaManager] setTrackEnabled(${kind}, ${enabled}) cancelled by user; not retrying`)
        return this.verifyGroundTruth(room, kind, !enabled)
      }

      console.error(`[MediaManager] setTrackEnabled(${kind}, ${enabled}) exhausted retries:`, err)

      // Fallback for audio/video (not screen share)
      if (kind !== 'screen') {
        const fallbackOk = await this.fallbackGetUserMedia(room, kind, enabled, options?.deviceId as string | undefined)
        if (fallbackOk) {
          console.info(`[MediaManager] Fallback ${kind} enable succeeded`)
          return this.verifyGroundTruth(room, kind, enabled)
        }
      }

      return this.verifyGroundTruth(room, kind, !enabled)
    }

    // When disabling, explicitly stop any live tracks that might have been
    // published via fallback or that LiveKit didn't stop immediately.
    if (!enabled) {
      const pub = this.getLocalPublication(participant, kind)
      if (pub?.track?.mediaStreamTrack && pub.track.mediaStreamTrack.readyState === 'live') {
        try { pub.track.stop() } catch {}
      }
    }

    const actual = this.verifyGroundTruth(room, kind, enabled)
    if (actual !== enabled) {
      console.warn(
        `[MediaManager] Ground truth mismatch for ${kind}: requested ${enabled}, got ${actual}. Attempting recovery...`,
      )
      try {
        // Zombie publication: flag says on but the underlying track is
        // `ended`. Just toggling setEnabled again won't help because
        // LiveKit short-circuits ("already enabled"). Unpublish the dead
        // track so the next setEnabled creates a fresh publication.
        if (enabled) {
          const stalePub = this.getLocalPublication(participant, kind)
          const staleTrack = stalePub?.track?.mediaStreamTrack
          if (stalePub?.track && (!staleTrack || staleTrack.readyState === 'ended')) {
            try {
              await participant.unpublishTrack(stalePub.track, true)
            } catch (err) {
              console.warn(`[MediaManager] unpublishTrack failed during recovery:`, err)
            }
          }
        }
        if (kind === 'audio') await participant.setMicrophoneEnabled(enabled, options as any)
        else if (kind === 'video') await participant.setCameraEnabled(enabled, options as any)
        else await participant.setScreenShareEnabled(enabled, options as any)
        return this.verifyGroundTruth(room, kind, enabled)
      } catch {
        return actual
      }
    }
    return actual
  }

  // ─────────────────────────────────────────────────────────────
  //  Verification
  // ─────────────────────────────────────────────────────────────

  verifyGroundTruth(room: Room, kind: MediaKind, expectedEnabled: boolean): boolean {
    const participant = room.localParticipant

    let flag: boolean
    if (kind === 'audio') flag = participant.isMicrophoneEnabled
    else if (kind === 'video') flag = participant.isCameraEnabled
    else flag = participant.isScreenShareEnabled

    const pub = this.getLocalPublication(participant, kind)
    const mediaTrack = pub?.track?.mediaStreamTrack
    let trackReady = !!pub && !pub.isMuted && mediaTrack?.readyState === 'live'
    let trackDead = !!pub && (!mediaTrack || mediaTrack.readyState === 'ended')

    // DFN/RNNoise hot-swap robustness: after `replaceTrack(handle.track)`
    // LiveKit's `pub.track.mediaStreamTrack` getter can return the PREVIOUS
    // (now-ended) MediaStreamTrack while audio is correctly routed at the
    // WebRTC sender layer to our enhanced track. Without this guard, the
    // zombie branch below mis-detected the publication as dead and triggered
    // an unpublish-republish cycle every health-check tick, audible as
    // ~50-100ms mic dropouts every few seconds. If our denoiser handle has
    // a live processed track, it's the source of truth.
    if (kind === 'audio' && this.denoiserHandle?.track?.readyState === 'live') {
      // Our denoiser is producing a LIVE processed track routed to the WebRTC
      // sender → audio IS flowing. But LiveKit's pub.track.mediaStreamTrack
      // getter can lag to the PREVIOUS (now-ended) track after a denoiser
      // hot-swap (replaceTrack) — pronounced on FIREFOX. That stale 'ended' read
      // made trackDead=true AND trackReady=false, so the mic-desync branch below
      // fired a FALSE reconcile → denoiser re-apply → track rebuild, heard as a
      // periodic "шип" (Gasan, Firefox, 2026-05-22). Trust the live processed
      // track for BOTH liveness and readiness (readiness only when the pub is
      // present + unmuted, so a genuine mute still reads as such).
      trackDead = false
      if (!!pub && !pub.isMuted) trackReady = true
    }

    if (flag !== expectedEnabled && !trackReady) {
      if (kind === 'audio' && expectedEnabled) {
        // Capture WHY the mic reads disabled while the user intends unmuted — the
        // flag-false desync branch that drives `health-audio-desync` recovery but
        // was never recorded (the zombie branch below has its own snapshot). `room`
        // is the LiveKit ConnectionState: 'connecting'/'reconnecting' here means the
        // publication never reached the SFU (Konstantin s2, 2026-06-29: ICE never
        // connected → forced refresh) vs a genuinely un-published / ended mic track.
        this._lastAudioDesyncDiag =
          `flag=${flag} pub=${!!pub} pubMuted=${pub?.isMuted ?? 'na'} ` +
          `trk=${mediaTrack?.readyState ?? 'none'} dn=${this._denoiserState} room=${room.state}`
      }
      return flag
    }
    if (flag !== expectedEnabled && trackReady) {
      console.warn(`[MediaManager] ${kind} track is live but flag says disabled — treating as enabled`)
      return true
    }
    // Zombie state: LiveKit thinks the track is enabled but the underlying
    // MediaStreamTrack is `ended` — happens after mute path stops the track
    // and unmute toggles only the flag without republishing. Peers won't
    // hear/see anything until a fresh publication. Surface as "disabled"
    // so setTrackEnabled's recovery path runs.
    if (expectedEnabled && flag && trackDead) {
      if (kind === 'audio') {
        // Snapshot why the 154A guard didn't save this (it only clears trackDead
        // when denoiserHandle.track is 'live'); surfaced via getDiagnosticCounters.
        this._lastAudioGtDiag =
          `mediaState=${mediaTrack?.readyState ?? 'null'} pubMuted=${pub?.isMuted ?? 'na'} ` +
          `dnExists=${!!this.denoiserHandle} dnTrack=${this.denoiserHandle?.track?.readyState ?? 'none'}`
      }
      console.warn(`[MediaManager] ${kind} flag=true but track is ended — zombie publication, surfacing as disabled`)
      return false
    }
    return flag
  }

  // ─────────────────────────────────────────────────────────────
  //  Health Check
  // ─────────────────────────────────────────────────────────────

  startHealthCheck(
    getState: () => {
      room: Room | null
      expectedMuted: boolean
      expectedVideoOn: boolean
      expectedScreenShare: boolean
    },
  ): void {
    this.stopHealthCheck()
    if (this.opts.healthCheckIntervalMs <= 0) return

    this.healthTimer = setInterval(() => {
      // Keep managed AudioContexts alive EVERY tick. A denoiser/playback context
      // can suspend MID-CALL on a FOREGROUND tab (Chromium audio power-saving,
      // output-device change) — neither the gesture nor the visibilitychange
      // resume path fires then, so the denoiser worklet stops and the published
      // mic goes SILENT (local_audio_level=0 = "перестал работать микрофон" +
      // DM "не слышно"). This is the clean root-cause recovery the legacy reapply
      // storm was crudely emulating with a costly ~60s republish: resume() here
      // is cheap + idempotent (no-op when running) and revives the SAME track in
      // ~1s with NO renegotiation, so it never drops a 1:1 DM peer. Shared RNNoise
      // context ⇒ covers BOTH channel and DM at once. Kill-switch:
      // localStorage 'voiceCtxResumeOnTick'='false'. Invariant #53.
      if (typeof localStorage === 'undefined' || localStorage.getItem('voiceCtxResumeOnTick') !== 'false') {
        try { resumeManagedAudioContexts() } catch { /* best-effort; never break the tick */ }
      }
      const state = getState()
      if (!state.room) return

      const checks: { kind: MediaKind; expected: boolean; label: string }[] = [
        { kind: 'audio', expected: !state.expectedMuted, label: 'mic' },
        { kind: 'video', expected: state.expectedVideoOn, label: 'camera' },
        { kind: 'screen', expected: state.expectedScreenShare, label: 'screen' },
      ]

      for (const { kind, expected, label } of checks) {
        const actual = this.verifyGroundTruth(state.room, kind, expected)
        if (actual === expected) {
          // Healthy — restore this kind's full budget for the next genuine
          // desync (so a real transient hiccup recovers on the very next tick).
          this._resetReconcile(kind)
          if (kind === 'audio') this._audioDesyncSince = 0 // clear stranded timer (#72)
          continue
        }
        // VIDEO auto-recovery OFF by default (2026-05-22). A desynced/zombie
        // video track here would republish (setTrackEnabled = SDP renegotiation)
        // which DISRUPTS audio — same root as the governor (#41). Audio-first
        // strategy: a broken camera STAYS broken until the user re-toggles it;
        // camera recovery is fixed separately. Audio + screen still auto-recover.
        // Re-enable via localStorage 'voiceVideoAutoRecover'='true'.
        if (
          kind === 'video' &&
          !(typeof localStorage !== 'undefined' && localStorage.getItem('voiceVideoAutoRecover') === 'true')
        ) {
          continue
        }
        // Desync. The recovery republishes the track — audio:
        // setMicrophoneEnabled → applyDenoiserMic → getUserMedia (also
        // re-establishes the denoiser per the user's saved pref); video/screen:
        // setTrackEnabled. Either way it's a destructive SDP renegotiation, so
        // gate it PER-KIND (shared 60 s backoff + 3-strike). A persistent
        // verifyGroundTruth false-positive would otherwise fire every 1 s tick:
        // audio = mic-jumping / "nobody audible" (#37); video = camera flicker
        // every second (#38).
        if (kind === 'audio') {
          // Track continuous-desync dwell + (gated, default-off) auto re-arm a
          // stranded mic whose recovery budget is spent. Runs BEFORE the gate so a
          // granted re-arm makes the very next `_allowReconcile('audio')` fire (#72).
          if (this._audioDesyncSince === 0) this._audioDesyncSince = Date.now()
          this._maybeRearmStrandedAudio()
        }
        if (!this._allowReconcile(kind)) continue
        this._healthRecoveryCount++
        this._lastRecoveryReason = `health-${kind}-desync`
        console.warn(
          `[MediaManager] Health check ${label} desync: expected=${expected}, actual=${actual}. ` +
            `Reconciling (attempt ${this._reconcileGate[kind].strikes}/${MediaManager.DENOISER_REAPPLY_MAX_STRIKES}).`,
        )
        if (kind === 'audio') {
          this.setMicrophoneEnabled(state.room, expected).catch(() => {})
        } else {
          this.setTrackEnabled(state.room, kind, expected).catch(() => {})
        }
      }

      // Denoiser integrity check. Mic-enabled flag can stay true (so
      // the verifyGroundTruth loop above sees no desync) while the
      // underlying publication has been re-routed through a raw
      // browser-mic track — either because some caller passed
      // setMicrophoneEnabled(true) without the noiseSuppression
      // option (pre-fix regression), because LiveKit re-published
      // during a reconnect, or because the denoiser graph was torn
      // down out from under us. Detect both cases:
      //   1. user wants NS + this.denoiserHandle is null
      //   2. user wants NS + the live publication's MediaStreamTrack
      //      no longer matches the denoiser output we last produced
      // and re-apply.
      //
      // Backoff (DENOISER_REAPPLY_BACKOFF_MS + DENOISER_REAPPLY_MAX_STRIKES):
      // see the comment on `_lastDenoiserReapplyAt` above. Repeated
      // re-applies were causing SDP renegotiations every 5 s, which
      // stalled inbound RTP, which fired remoteAudioWatchdog
      // re-subscribes, which the user heard as continuous stutter.
      // Now we re-apply at most once per minute and give up after
      // three consecutive failures — the next legitimate user
      // action (mute toggle, device switch, etc.) will reset the
      // counter via `_resetDenoiserReapplyState` and we'll try again.
      const wantsNS = typeof window !== 'undefined' && localStorage.getItem('noiseSuppression') !== 'false'
      // ⚠ HOTFIX 1.1.160A — the #52 `roomCtxMicLive` guard was REVERTED here.
      // It skipped this denoiser-integrity reapply whenever the per-room mic
      // track read 'live'. But that reapply (the legacy "health-denoiser-inactive"
      // storm) turned out to be LOAD-BEARING: it rebuilt the mic every ~60s and
      // was masking a per-room sharedMicCapture denoiser whose AudioContext
      // suspends to SILENCE. With the storm gone the client published a
      // live-but-SILENT track forever (prod 159A telemetry: local_audio_level=0,
      // total_energy=0, health_recovery_count=0 → "перестал работать микрофон").
      // Restored the original probe so the mic self-heals. The proper DM-storm
      // fix must RESUME/rebuild the per-room denoiser context (#16/#18), NOT skip
      // recovery — follow-up; see VOICE_CRITICAL_INVARIANTS #52.
      //
      // ⚠ 2026-05-26 (1.1.171A) — #52 GUARD RE-INSTATED behind a kill-switch.
      // The load-bearing storm is no longer required: #53
      // (`resumeManagedAudioContexts` every health tick) directly resumes any
      // suspended denoiser AudioContext in 1 s without `getUserMedia`
      // renegotiation. Without the guard, the legacy storm fires every ~60 s
      // even when per-room sharedMicCapture is the actual owner — its
      // `applyDenoiserMic` republish then RACES the per-room published track
      // (`could not find published track` + `TrackSubscriptionFailed` +
      // `unpublishing track` in Dmitriy's 170A console), and frequently
      // pre-empts #58's `backgroundDenoiserSwap` so per-room callers stay
      // stuck reporting `denoiser='agc-only'` while the published track is
      // a different (legacy) graph. Diagnosed from prod 2026-05-26 5-min
      // sweep: 986 `health-denoiser-inactive` recoveries across 6 users,
      // Aleksei I + Dmitriy 100% `agc-only` in voice_diag despite the
      // legacy log line `[MediaManager] Denoiser: rnnoise` firing right
      // after `setMicrophoneEnabled.done`. With #53 + #57 + #58 in place,
      // legacy storm no longer carries any safety value when per-room
      // owns the mic — it's pure harm. Kill-switch:
      // `localStorage 'voiceLegacyHealthRepublish'='on'` restores the
      // pre-171A always-republish behaviour for diagnostics.
      // Skip the integrity probe when an OS-level denoiser (Krisp /
      // NVIDIA Broadcast) is the source of truth — we deliberately
      // have no handle in that case and "fixing" the absent handle
      // would mean republishing on top of the already-clean track.
      if (wantsNS && !this._osDenoiserActive && state.room.localParticipant.isMicrophoneEnabled) {
        // PER-ROOM GUARD (#59 — re-instated #52, safe because #53 resumes
        // suspended contexts non-destructively each tick). When the per-room
        // sharedMicCapture has published a live mic track, the singleton
        // `this.denoiserHandle` is null BY DESIGN (per-room path doesn't
        // populate it). The legacy republish would override the per-room
        // track via replaceTrack → SDP renegotiation / TrackSubscriptionFailed
        // / pre-empt the in-progress #58 swap. Skip the legacy probe in
        // that case; per-room owns its own lifecycle (#57 AGC-only terminal
        // fallback + #58 AGC-only stopgap + background swap + #53 ctx resume).
        const perRoomOwnsMic =
          typeof window === 'undefined' || localStorage.getItem('voiceLegacyHealthRepublish') !== 'on'
            ? this.contexts.get(state.room)?.getPublishedMicTrack()?.readyState === 'live'
            : false
        if (perRoomOwnsMic) {
          // Clear any leftover legacy strike state so a future per-room
          // teardown that falls back to the legacy path gets a clean budget.
          // Fall through to DFN promotion / CPU governor / audio+video
          // reconcile below — only the integrity-probe re-apply is skipped.
          this._consecutiveDenoiserReapplyFailures = 0
        } else {
        const pub = this.getLocalPublication(state.room.localParticipant, 'audio')
        const liveTrack = pub?.track?.mediaStreamTrack
        const expectedTrack = this.denoiserHandle?.track
        // Trust by liveness, not reference equality. LiveKit's
        // replaceTrack() updates the WebRTC sender but may keep
        // pub.track.mediaStreamTrack pointing at the original track —
        // `liveTrack !== expectedTrack` was firing constantly for DFN
        // (which always uses the apply→replaceTrack path) and triggering
        // a republish every minute. Users heard a ~50-100ms mic dropout
        // each cycle. Real failure modes we still catch:
        //   • denoiser graph torn down (handle gone)
        //   • our processed track ended (worker crashed, ctx closed)
        //   • live publication's track ended (peer connection died)
        // Re-apply ONLY when OUR denoiser graph is genuinely broken (handle gone
        // or our processed track ended). When our denoiser OUTPUT is live, audio
        // is flowing to the sender — so a stale liveTrack-ended read (LiveKit's
        // pub.track.mediaStreamTrack getter lagging to the previous ended track
        // after a replaceTrack hot-swap, pronounced on FIREFOX) is a FALSE signal
        // that fired a re-apply every cooldown → periodic "шип" (Gasan, FF). A
        // real dead publication (PC death) is handled by LiveKit reconnect + the
        // mic-desync check, not by re-applying the denoiser.
        const denoiserOutputLive = expectedTrack?.readyState === 'live'
        const integrityFail =
          !expectedTrack ||
          expectedTrack.readyState === 'ended' ||
          (!denoiserOutputLive && (!liveTrack || liveTrack.readyState === 'ended'))
        if (integrityFail) {
          const nowMs = Date.now()
          const sinceLast = nowMs - this._lastDenoiserReapplyAt
          if (this._consecutiveDenoiserReapplyFailures >= MediaManager.DENOISER_REAPPLY_MAX_STRIKES) {
            // We've already tried 3 times with no measurable change.
            // Stop pestering the user with renegotiation churn. Next
            // user-driven mic op resets the counter and we try again.
            return
          }
          if (sinceLast < MediaManager.DENOISER_REAPPLY_BACKOFF_MS) {
            // Within the cooldown window — skip silently. We logged
            // the warning on the last attempt; another log line
            // every 5 s adds noise without adding signal.
            return
          }
          this._lastDenoiserReapplyAt = nowMs
          this._consecutiveDenoiserReapplyFailures += 1
          this._healthRecoveryCount++
          this._lastRecoveryReason = 'health-denoiser-inactive'
          console.warn(
            `[MediaManager] Health check: denoiser not active despite user pref. ` +
              `Re-applying (attempt ${this._consecutiveDenoiserReapplyFailures}/${MediaManager.DENOISER_REAPPLY_MAX_STRIKES}).`,
          )
          this._reapplyFromHealthCheck(state.room).catch(() => {})
        } else {
          // Integrity passes — clear the strike counter so a future
          // legitimate hiccup (LiveKit reconnect, etc.) gets its
          // full 3-strike budget again.
          this._consecutiveDenoiserReapplyFailures = 0
        }
        } // end else (legacy probe branch — per-room guard #59 above)
      }

      // (Previously: DFN promotion + DFN stable-reset blocks here. Both
      // removed in 1.1.177A with the DFN backend itself.)

      // ── CPU-driven video downscale (audio-first) ─────────────────────
      // DISABLED BY DEFAULT (2026-05-22). The governor downscaled outgoing video
      // under sustained CPU load to free CPU for audio/DFN — but every change is
      // `setCameraEnabled(true)` = an SDP RENEGOTIATION, and that republish
      // DISRUPTS the very audio it's meant to protect. On a machine with bursty
      // Compute-Pressure (Sergey QA: nominal↔critical every few seconds) it
      // caused recurring "camera refreshes → audio drops", even after the
      // anti-flap dwell (#41) bounded it to 1/90s (still 1 audio hit/90s).
      // Audio under load is ALREADY protected by the DFN→RNNoise degrade (#7),
      // which is a cheap `replaceTrack` (NOT a renegotiation). So the governor
      // is both redundant and net-harmful → off by default. Re-enable for
      // experiments via localStorage 'voiceCpuVideoGovernor'='true'.
      {
        const governorOn =
          typeof localStorage !== 'undefined' && localStorage.getItem('voiceCpuVideoGovernor') === 'true'
        const camOn = governorOn && state.expectedVideoOn && state.room.localParticipant.isCameraEnabled
        const nowMs = Date.now()
        if (!camOn) {
          // Governor off (or no camera) — clear the override so video uses the
          // user's preset; never republish from here.
          this._cpuVideoOverride = null
          this._cpuBusySince = 0
          this._cpuNominalSince = 0
        } else if (isCpuBusy()) {
          this._cpuNominalSince = 0
          if (this._cpuBusySince === 0) this._cpuBusySince = nowMs
          if (
            !this._cpuVideoOverride &&
            readVideoQuality() !== 'low' && // user already on low — nothing to drop
            nowMs - this._cpuBusySince >= MediaManager.CPU_VIDEO_DOWNSCALE_MS &&
            nowMs - this._lastCpuVideoChangeAt >= MediaManager.CPU_VIDEO_MIN_DWELL_MS // anti-flap
          ) {
            this._cpuVideoOverride = 'low'
            this._lastCpuVideoChangeAt = nowMs
            console.warn('[MediaManager] CPU busy — dropping outgoing video to low (audio-first).')
            if (nowMs - this._lastCpuVideoToastAt > 60_000) {
              this._lastCpuVideoToastAt = nowMs
              try {
                useToastStore.getState().showToast({
                  id: 'cpu-video-downscale',
                  type: 'message',
                  title: 'Видео снижено',
                  body: 'Высокая нагрузка на процессор — качество видео временно снижено, чтобы звук остался чистым.',
                })
              } catch { /* toast is best-effort */ }
            }
            this.setCameraEnabled(state.room, true).catch(() => {})
          }
        } else {
          this._cpuBusySince = 0
          // Restore only on sustained 'nominal' (strict); 'fair' holds.
          if (this._cpuVideoOverride && getCpuPressure() === 'nominal') {
            if (this._cpuNominalSince === 0) this._cpuNominalSince = nowMs
            if (
              nowMs - this._cpuNominalSince >= MediaManager.CPU_VIDEO_RESTORE_MS &&
              nowMs - this._lastCpuVideoChangeAt >= MediaManager.CPU_VIDEO_MIN_DWELL_MS // anti-flap
            ) {
              this._cpuVideoOverride = null
              this._cpuNominalSince = 0
              this._lastCpuVideoChangeAt = nowMs
              console.info('[MediaManager] CPU nominal — restoring outgoing video quality.')
              this.setCameraEnabled(state.room, true).catch(() => {})
            }
          } else {
            this._cpuNominalSince = 0
          }
        }
      }
    }, this.opts.healthCheckIntervalMs)
  }

  stopHealthCheck(): void {
    if (this.healthTimer) {
      clearInterval(this.healthTimer)
      this.healthTimer = null
    }
  }

  cleanup(): void {
    this.stopHealthCheck()
    this.destroyDenoiser()
    // The applied-quality marker is per-Room; resetting on cleanup
    // makes sure a stale value never short-circuits the unpublish on
    // the next room's first camera enable.
    this.appliedVideoQuality = null
  }

  // ─────────────────────────────────────────────────────────────
  //  Fallback: raw getUserMedia + manual track publish
  // ─────────────────────────────────────────────────────────────

  private async fallbackGetUserMedia(
    room: Room,
    kind: 'audio' | 'video',
    enabled: boolean,
    deviceId?: string,
  ): Promise<boolean> {
    if (!enabled) {
      try {
        if (kind === 'audio') await room.localParticipant.setMicrophoneEnabled(false)
        else await room.localParticipant.setCameraEnabled(false)
        return true
      } catch {
        return false
      }
    }

    try {
      const constraints: MediaStreamConstraints =
        kind === 'audio'
          ? {
              audio: {
                echoCancellation: true,
                autoGainControl: true,
                ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
              },
            }
          : {
              video: {
                ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
              },
            }

      if (kind === 'audio') this._gumCount++
      // OSPROD-145: route video gUM through the camera-permission circuit
      // breaker. Denial here marks the sticky flag so the next attempt
      // (from UI dropdown or pre-join) shorts out instead of re-spamming
      // the browser. Audio path is untouched — it has its own dedicated
      // flow through applyDenoiserMic with retry semantics we don't want
      // to circuit-break.
      const stream =
        kind === 'audio'
          ? await navigator.mediaDevices.getUserMedia(constraints)
          : await requestVideoStream(constraints)
      const track = kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]
      if (!track) return false

      await room.localParticipant.publishTrack(track, {
        source: kind === 'audio' ? Track.Source.Microphone : Track.Source.Camera,
      })
      return true
    } catch (err) {
      console.error(`[MediaManager] Fallback getUserMedia(${kind}) failed:`, err)
      return false
    }
  }

  private getLocalPublication(participant: Room['localParticipant'], kind: MediaKind): LocalTrackPublication | undefined {
    return Array.from(participant.trackPublications.values()).find((pub): pub is LocalTrackPublication => {
      if (kind === 'audio') {
        return pub.kind === Track.Kind.Audio && pub.source === Track.Source.Microphone
      }
      if (kind === 'video') {
        return pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera
      }
      return pub.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare
    })
  }
}

export const mediaManager = new MediaManager()
